/**
 * Regulatory section — survey data, citations, complaints.
 *
 * Layout:
 *   • Page header (title + subtitle + Renewal/Expiration date strip).
 *   • Single container with the Acuity / Survey tab strip (atlas-ui
 *     `Tabs` with `button-minimal` — Acuity first per OR-1328) and an
 *     "+ Add" control on the right (Row | Column dropdown on Acuity).
 *   • Survey tab — full Figma table (node `Orion - Regulatory`
 *     335:11363): five column groups (Survey Costs + 2023/2022/2021/
 *     2020), two header rows, sticky # / Facility Name on the left and
 *     a sticky trash column on the right, plus per-year summary
 *     footers (Equivalency + Assoc. Premium).
 *   • Acuity tab (OR-1328 redesign) — ONE expandable row per facility:
 *     collapsed shows the derived "All" census per year; expanded adds
 *     an editable sub-row per care type (Skilled / Assisted /
 *     Independent / Add'l. Exposure). Year columns are dynamic: data-
 *     driven, user-addable (+ Add → Column), reorderable (drag or the
 *     header context menu — order persists per submission in
 *     localStorage; no backend preferences endpoint yet) and deletable
 *     (stages every record of that year for deletion, flushed on
 *     Save). A "Beds Sum" footer totals each year, and a dismissible
 *     "Missing Data Detected" banner + per-facility warning icon flag
 *     years with no data (triggered on page load and on Save).
 *
 * Data sources (Figma "Single source of truth for facilities"):
 *   • Survey tab → row-per-facility from the shared `useFacilities`
 *     SoT (same hook Schedule + UW Information consume). CMS surveys
 *     from `/v2/surveys/?submission_id=…` populate the per-year
 *     buckets via `src/api/adapters/regulatory.ts`. Add Row creates a
 *     facility — propagates to Schedule + UW Info automatically.
 *     Delete removes the facility (gated to non-CMS rows so we don't
 *     orphan ingested data).
 *   • Acuity tab → `/v2/occupancies/?submission_id=…` grouped per
 *     facility (full care-type × year census grid) via
 *     `aggregateOccupancies`. Cell edits PATCH the (facility, year)
 *     record — or POST one when the year had none — through the
 *     merge-aware write path (OR-1152).
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
    AlertCircle,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    Building07,
    Calendar,
    ChevronSelectorVertical,
    Columns01,
    DotsVertical,
    Plus,
    Rows01,
    Trash02,
} from "@untitledui/icons";
import {
    Button as AriaButton,
    Menu as AriaMenu,
    MenuItem as AriaMenuItem,
    MenuTrigger as AriaMenuTrigger,
    Popover as AriaPopover,
} from "react-aria-components";
import { Button, SearchSelect, Tabs, Tooltip, TooltipTrigger, cx } from "@tricura/atlas-ui";
import { EditableDateCell, EditableTextCell, InvalidShell } from "../../../components/editable-cell";
import { FacilityNameAutocomplete } from "../../../components/facility-name-autocomplete";
import { MissingDataBanner } from "../../../components/missing-data-banner";
import { StickyColDivider } from "../../../components/sticky-col-divider";
import { SubmissionFooter } from "../../../components/submission-footer";
import { TableEmptyState } from "../../../components/table-empty-state";
import { useFacilities } from "../../../hooks/use-facilities";
import { useSubmissionContext } from "../context";
import { getNextSection } from "../sections";
import { createFacility, createOccupancy, createSurvey, deleteOccupancy, deleteSurvey, listLocations, listOccupancies, listSurveys, patchOccupancy } from "../../../api";
import { ACUITY_CARE_TYPES, aggregateOccupancies, aggregateSurveys } from "../../../api/adapters/regulatory";
import {
    applyStoredYearOrder,
    bedsSumForYear,
    facilityYearTotal,
    isValidYearLabel,
    missingYearsByFacility,
    readStoredYearOrder,
    storeYearOrder,
} from "./acuity-columns";
import type { CatalogFacility, OccupancyCreateWire, OccupancyUpdateWire } from "../../../api";
import type { Facility, FacilityWritePayload } from "../../../types/facility";
import type { SubmissionLocationWire } from "../../../types/submission";
import type { SurveyCreateWire } from "../../../types/survey";

/** Catalog facility → SubmissionLocation create payload. Creating the
 *  location links the catalog facility to THIS submission (`facility_id`),
 *  which is how surveys/occupancies scope to the submission, and propagates
 *  the facility to the Schedule of Locations (the SoT). */
function catalogToFacilityPayload(cf: CatalogFacility): FacilityWritePayload {
    return {
        catalogFacilityId: cf.id,
        name: cf.name,
        state: cf.state ?? "",
        providerNumber: cf.cms_number ?? "",
        address: cf.address ?? "",
        city: cf.city ?? "",
        zip: cf.zip_code ?? "",
    };
}
import { draftToOccupancyWrites, friendlyAcuitySaveError, occupancyIdByFacilityYear, occupancyKey } from "./acuity-draft-writes";
import { ARCHIVED_ROW } from "../../../components/facility-archive-row";
import { FacilityRowActionsMenu } from "../../../components/facility-row-actions-menu";
import { acuityRowMatchesCatalog, findFacilityForRow, locationRepresentsCatalog } from "./facility-dedup";
import type { AcuityRow, SurveyFacilityRef, SurveyFooterTotals, SurveyRow, YearMetrics } from "./types";

const US_STATES = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
].map((abbr) => ({ id: abbr, label: abbr }));

/** Editable draft for a user-added Survey row (string buffers for inputs).
 *  One draft = one survey POSTed on Save. The Last Survey date sets the
 *  real year/`survey_date`; the metrics live alongside it. */
interface NewSurveyDraft {
    id: string;
    facilityId: string;
    facilityName: string;
    state: string;
    /** The picked catalog facility — used to create a SubmissionLocation
     *  (so the survey links to this submission + propagates to Schedule). */
    catalog?: CatalogFacility;
    fineCount: string;
    totalFines: string;
    lastSurvey: string;
    severityCitations: string;
    citationCount: string;
    complaintCount: string;
    explanation: string;
}

function makeNewSurveyDraft(): NewSurveyDraft {
    return {
        id: `local-${crypto.randomUUID()}`,
        facilityId: "",
        facilityName: "",
        state: "",
        fineCount: "",
        totalFines: "",
        lastSurvey: "",
        severityCitations: "",
        citationCount: "",
        complaintCount: "",
        explanation: "",
    };
}

/** "MM/DD/YYYY" → ISO "YYYY-MM-DD" for the wire, or null if not a real date. */
function formDateToIso(value: string): string | null {
    const m = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;
    const [, mm, dd, yyyy] = m;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (d.getFullYear() !== Number(yyyy) || d.getMonth() !== Number(mm) - 1 || d.getDate() !== Number(dd)) return null;
    return `${yyyy}-${mm}-${dd}`;
}

/** Parse a user-typed integer ("12", "0", "") → number | null. */
function toIntOrNull(v: string): number | null {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? Math.round(n) : null;
}

/** Money string ("$1,234.50" / "1234.5") → decimal wire string, or null. */
function toDecimalStringOrNull(v: string): string | null {
    const t = v.replace(/[^0-9.-]/g, "");
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n.toFixed(2) : null;
}

/** Build the POST body for a survey draft. Returns null when there's not
 *  enough to create one (the backend needs at least a facility + date). */
function draftToSurveyCreate(draft: NewSurveyDraft): SurveyCreateWire | null {
    // Only a facility is required to attach a survey to the submission; the
    // date is optional (a dateless survey still shows, just without a year
    // bucket).
    if (!draft.facilityId) return null;
    return {
        facility_id: draft.facilityId,
        survey_date: formDateToIso(draft.lastSurvey),
        num_fines: toIntOrNull(draft.fineCount),
        total_incurred_fines: toDecimalStringOrNull(draft.totalFines),
        num_severity_citations: toIntOrNull(draft.severityCitations),
        num_citations: toIntOrNull(draft.citationCount),
        num_complaint_surveys: toIntOrNull(draft.complaintCount),
        explanation: draft.explanation.trim() || null,
    };
}

// ─── Acuity (occupancies) — Add Row drafts ──────────────────────────────────

/** Non-negative integer cells (bed counts, fine/citation/complaint counts)
 *  accept digits only — strip anything else so `-`, `+`, `.`, `=`, letters
 *  can't be typed (the backend rejects negatives/symbols with a 422,
 *  OR-1416; applied across Acuity + Survey to prevent the same class of bug). */
const sanitizeCount = (raw: string) => raw.replace(/\D/g, "");

/** Non-negative money cells (e.g. Total Fines) accept digits + a single
 *  decimal point — no `-`, `+`, `=`, `$`, `,`, letters. */
const sanitizeMoney = (raw: string) => {
    const cleaned = raw.replace(/[^\d.]/g, "");
    const dot = cleaned.indexOf(".");
    return dot === -1 ? cleaned : cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, "");
};

/** Care-type → backend occupancy bed-count field. */
const ACUITY_TYPE_OPTIONS = [
    { id: "skilled_beds", label: "Skilled" },
    { id: "assisted_beds", label: "Assisted" },
    { id: "independent_beds", label: "Independent" },
    { id: "additional_beds", label: "Add'l. Exposure" },
] as const;

type AcuityBedField = (typeof ACUITY_TYPE_OPTIONS)[number]["id"];

/** Editable draft for a user-added Acuity row (one facility × care type,
 *  a census value per year). On save each filled year POSTs an occupancy. */
interface NewAcuityDraft {
    id: string;
    facilityId: string;
    facilityName: string;
    /** Picked catalog facility — used to create a SubmissionLocation so the
     *  occupancy links to this submission + propagates to Schedule. */
    catalog?: CatalogFacility;
    /** Backend bed field (skilled_beds / assisted_beds / …). */
    type: AcuityBedField | "";
    state: string;
    /** year → input string. */
    values: Record<string, string>;
}

function makeNewAcuityDraft(): NewAcuityDraft {
    return {
        id: `local-${crypto.randomUUID()}`,
        facilityId: "",
        facilityName: "",
        type: "",
        state: "",
        values: {},
    };
}

/** Empty (facility, year) index — with it, `draftToOccupancyWrites` routes
 *  every filled year to `creates`, which makes it double as the "≥1 year
 *  value" validation probe. */
const EMPTY_OCC_INDEX = new Map<string, string>();

const SECTION_ID = "regulatory";

type RegulatoryTab = "survey" | "acuity";

// Acuity first (OR-1328) — it's the tab underwriters work in most.
const REGULATORY_TABS: { id: RegulatoryTab; label: string; autotest: string }[] = [
    { id: "acuity", label: "Acuity", autotest: "regulatory-tabs-acuity" },
    { id: "survey", label: "Survey", autotest: "regulatory-tabs-survey" },
];

// View-layer types live in ./types so the adapter can reuse them without
// pulling this file's UI deps. `YearMetrics` is re-exported below because
// the per-year `compareSurvey` switch indexes it by key.

// ─── Survey table — column registry ────────────────────────────────────────

/** Static columns under the "Survey Costs" group — these don't repeat
 *  per year. `width` is also applied as `minWidth` so the rhythm stays
 *  stable while the wrapping card narrows. */
const COST_COLUMNS = [
    { id: "index", label: "#", width: 80, sticky: "left-0" as const },
    { id: "facilityName", label: "Facility Name", width: 240, sticky: "left-80" as const },
    { id: "state", label: "State", width: 120, sticky: undefined },
    { id: "fineCount", label: "# Fines", width: 120, sticky: undefined },
    { id: "totalFines", label: "Total Fines", width: 140, sticky: undefined },
] as const;

/** Columns repeated under each year group. The id becomes
 *  `{year}.{id}` once flattened into a sortable field. */
const YEAR_COLUMNS = [
    { id: "lastSurvey", label: "Last Survey", width: 140 },
    { id: "severityCitations", label: "Severity Citations", width: 180 },
    { id: "citationCount", label: "# Citations", width: 140 },
    { id: "complaintCount", label: "# Complaints", width: 140 },
    { id: "explanation", label: "Explanation", width: 200 },
] as const;

/** "lastSurvey" → "last-survey" — for a stable data-autotest per YEAR_COLUMNS
 *  entry, independent of the year itself (which isn't static). */
const camelToKebab = (s: string) => s.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

// Survey year columns are data-driven (see `deriveSurveyYears`).
type SurveyYear = string;

/** Width of the trailing row-actions column — kept narrow but wide
 *  enough to host the 32px icon button with comfortable padding. */
const TRASH_COL_W = 64;

/** Stable reference for the not-yet-loaded facilities list, so the memo that
 *  derives archived state doesn't churn every render. */
const EMPTY_FACILITIES: Facility[] = [];

// ─── Acuity table — column registry ────────────────────────────────────────

// Fixed columns per the OR-1328 Figma: # / Facility Name / State / Type.
// Only # (and the year columns) sort — the Figma shows no sort affordance
// on Facility Name / State, and Type is meaningless to order by (every
// main row reads "All"; the care types live in the expandable sub-rows).
const ACUITY_BASE_COLUMNS = [
    { id: "index", label: "#", width: 80, sticky: "left-0" as const, sortable: true },
    { id: "facilityName", label: "Facility Name", width: 240, sticky: "left-80" as const, sortable: false },
    { id: "state", label: "State", width: 120, sticky: undefined, sortable: false },
    { id: "type", label: "Type", width: 140, sticky: undefined, sortable: false },
] as const;

/** The Acuity AND Survey year columns are **data-driven**: they come from
 *  the years the backend actually returns for this submission (newest
 *  first), so the tables never silently drop a year nor invent an empty
 *  one. (Before, a hardcoded Figma list filtered the data and quietly
 *  discarded any year not in it — e.g. 2024/2025.) When a submission has
 *  no data yet, fall back to a continuous window of the last few years so
 *  the "Add Row" flow still has columns to fill in. */
const RECENT_YEARS_SPAN = 5;
function fallbackRecentYears(): string[] {
    const now = new Date().getFullYear();
    return Array.from({ length: RECENT_YEARS_SPAN }, (_, i) => String(now - i));
}
/** Distinct years present in the occupancy wire rows, newest first. */
function deriveAcuityYears(wire: readonly { year: number }[]): string[] {
    const years = [...new Set(wire.map((r) => String(r.year)))];
    return years.length === 0 ? fallbackRecentYears() : years.sort().reverse();
}
/** Distinct years present in the survey wire rows (from `survey_date`),
 *  newest first. Same data-driven rule as Acuity. */
function deriveSurveyYears(wire: readonly { survey_date: string | null }[]): string[] {
    const years = [...new Set(wire.map((s) => (s.survey_date ?? "").slice(0, 4)).filter((y) => /^\d{4}$/.test(y)))];
    return years.length === 0 ? fallbackRecentYears() : years.sort().reverse();
}
type AcuityYear = string;

const ACUITY_YEAR_COL_W = 100;

// ─── Sort ───────────────────────────────────────────────────────────────────

type SurveySortField =
    | (typeof COST_COLUMNS)[number]["id"]
    | `${SurveyYear}.${(typeof YEAR_COLUMNS)[number]["id"]}`;

type AcuitySortField = "index" | AcuityYear;

interface SortState<F extends string> {
    field: F;
    dir: "asc" | "desc";
}

function parseMoney(s: string): number {
    const cleaned = s.replace(/[^0-9.-]/g, "");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
}

function parseDate(s: string): number {
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return 0;
    return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2])).getTime();
}

function compareSurvey(a: SurveyRow, b: SurveyRow, field: SurveySortField): number {
    if (field.includes(".")) {
        const [year, key] = field.split(".") as [SurveyYear, keyof YearMetrics];
        const av = a.years[year][key];
        const bv = b.years[year][key];
        if (key === "lastSurvey") return parseDate(av as string) - parseDate(bv as string);
        if (typeof av === "number" && typeof bv === "number") return av - bv;
        return String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
    }
    switch (field) {
        case "index":
        case "fineCount":
            return (a[field] as number) - (b[field] as number);
        case "totalFines":
            return parseMoney(a.totalFines) - parseMoney(b.totalFines);
        default: {
            const key = field as "facilityName" | "state";
            return String(a[key] ?? "").toLowerCase().localeCompare(String(b[key] ?? "").toLowerCase());
        }
    }
}

function compareAcuity(a: AcuityRow, b: AcuityRow, field: AcuitySortField): number {
    // Year fields look like "2026" / "2023" / etc. — purely numeric strings.
    // They order by the facility's derived "All" total for that year.
    if (/^\d{4}$/.test(field)) {
        return (facilityYearTotal(a, field) ?? 0) - (facilityYearTotal(b, field) ?? 0);
    }
    // "index" is the only sortable base column (no sort on Facility
    // Name / State / Type per the Figma).
    return a.index - b.index;
}

// ─── Sticky-column class helpers ────────────────────────────────────────────

/** Sticky-left class set keyed on the column position. The `left-[80px]`
 *  literal here is intentional — Tailwind v4 needs explicit values for
 *  sticky positioning offsets and 80 matches the # column's width. */
// The `left-80` column (Facility Name) is the last pinned one — it hosts the
// <StickyColDivider/> line so the fixed columns stay separated from the
// scrolling data on horizontal scroll (Andressa review).
// Mobile (`<md`): sticky columns are dropped per OR-1280 — the fixed
// `#` + `Facility Name` cols (320px) would otherwise blanket a narrow
// viewport and hide the scrolling data columns. Desktop (`md+`) keeps
// the freeze unchanged. Same convention as the Staffing Ratios table.
const STICKY_HEAD: Record<string, string> = {
    "left-0": "md:sticky md:left-0 md:z-20 md:bg-secondary",
    "left-80": "md:sticky md:left-[80px] md:z-20 md:bg-secondary",
};
const STICKY_CELL: Record<string, string> = {
    "left-0": "md:sticky md:left-0 md:z-10 md:bg-primary",
    "left-80": "md:sticky md:left-[80px] md:z-10 md:bg-primary",
};

/** Sticky offset for the section labels in the table header's first
 *  row. Pegs them just past the sticky `#` + `Facility Name` cols
 *  (80 + 240 = 320 px) instead of behind them at viewport-x=0. */
const STICKY_SECTION_OFFSET = "md:left-[320px]";
const STICKY_RIGHT_HEAD = "md:sticky md:right-0 md:z-20 md:bg-secondary";
const STICKY_RIGHT_CELL = "md:sticky md:right-0 md:z-10 md:bg-primary";

// ─── Component ──────────────────────────────────────────────────────────────

export function RegulatoryPage() {
    const { detail } = useSubmissionContext();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<RegulatoryTab>("acuity");

    // Shared facilities SoT (same hook Schedule uses). Read here to resolve each
    // row's facility — for the name, the archived styling and the Add Row dedup.
    // Archiving/restoring a facility from here writes through the same `update`
    // Schedule calls, cascading everywhere else that reads `useFacilities`. The
    // save is also wired because Add Row creates a location (OR-1298).
    const {
        state: facilitiesState,
        update: updateFacility,
        save: saveFacilities,
        isDirty: facilitiesDirty,
    } = useFacilities(detail.id);

    const facilityList = useMemo(
        () => (facilitiesState.status === "ready" ? facilitiesState.data : EMPTY_FACILITIES),
        [facilitiesState],
    );

    /** True when the location behind a Regulatory row is archived (OR-1431):
     *  the row still renders as historical data, struck through and inert. */
    const isRowArchived = useCallback(
        (row: { facilityId: string; facilityName: string }) =>
            findFacilityForRow(facilityList, row)?.isExcluded === true,
        [facilityList],
    );

    /** Resolves the facility behind a Regulatory row, if any — some rows (a
     *  catalog facility never added to Schedule) have none, and get Delete
     *  only rather than an Archive item that would silently do nothing. */
    const getFacilityForRow = useCallback(
        (row: { facilityId: string; facilityName: string }) => findFacilityForRow(facilityList, row),
        [facilityList],
    );

    const setFacilityArchived = useCallback(
        (facilityId: string, isExcluded: boolean) => void updateFacility(facilityId, { isExcluded }),
        [updateFacility],
    );

    // Surveys carry only `facility_id`. Facility Name / State (and the
    // footer Equivalency / Assoc. Premium) DON'T live on the survey row —
    // they come from the submission's Schedule locations (the SoT), joined
    // by `facility_id`. So we load `/v2/submissions/{id}/locations/`
    // alongside the surveys. Empty surveys → Empty State.
    const [surveyRows, setSurveyRows] = useState<SurveyRow[]>([]);
    // Year columns rendered by the Survey table — derived from the data.
    const [surveyYears, setSurveyYears] = useState<string[]>(() => fallbackRecentYears());
    const [footerTotals, setFooterTotals] = useState<SurveyFooterTotals>({ equivalency: 0, assocPremium: 0 });
    const [surveyLoading, setSurveyLoading] = useState<boolean>(true);
    const [surveyError, setSurveyError] = useState<string | null>(null);
    // User-added Survey rows (editable drafts) — not yet POSTed. Kept apart
    // from the loaded read-only rows so we never diff/clobber CMS data.
    const [newSurveyDrafts, setNewSurveyDrafts] = useState<NewSurveyDraft[]>([]);
    // User-added Acuity rows (editable drafts) — POSTed on Save & Continue.
    const [newAcuityDrafts, setNewAcuityDrafts] = useState<NewAcuityDraft[]>([]);
    const [savingSurvey, setSavingSurvey] = useState<boolean>(false);
    // Request-failure message for the Add-Row flow (network/500). Validation
    // is shown INLINE per cell (project pattern), gated by `showErrors` which
    // flips on the first Save attempt — never a banner under the tabs.
    const [saveError, setSaveError] = useState<string | null>(null);
    const [showErrors, setShowErrors] = useState<boolean>(false);

    const [acuityRows, setAcuityRows] = useState<AcuityRow[]>([]);
    // Last-loaded snapshot — the baseline Save diffs the edited census
    // against (so only changed cells PATCH/POST their occupancy).
    const [acuityBaseline, setAcuityBaseline] = useState<AcuityRow[]>([]);
    const [acuityYears, setAcuityYears] = useState<string[]>(() => fallbackRecentYears());
    const [acuityLoading, setAcuityLoading] = useState<boolean>(true);
    const [acuityError, setAcuityError] = useState<string | null>(null);
    // Occupancy record ids staged for deletion by the Acuity trash (whole
    // facility) or by Delete Column. Nothing is called until Save — same
    // staged semantics as the SubmissionLocation removal (OR-1152).
    const [stagedOccupancyDeletes, setStagedOccupancyDeletes] = useState<string[]>([]);
    // Years the user added as columns this session that have no backend
    // records yet. FE-only by design: an empty column doesn't survive a
    // reload — only cells the user fills in persist (as occupancies). A ref
    // (not state): only the loader's year-merge reads it, never the render.
    const manualYearsRef = useRef<string[]>([]);
    // In-progress "new year column" header input: its value and the index
    // in `acuityYears` it inserts at on confirm. Null = no draft column.
    const [yearDraft, setYearDraft] = useState<{ value: string; insertAt: number } | null>(null);
    const [yearDraftError, setYearDraftError] = useState<string | null>(null);
    // "Missing Data Detected" banner visibility — (re)triggered on page
    // load and on Save per the OR-1328 design; the X dismisses until the
    // next trigger. Only renders while some facility actually has gaps.
    const [missingAlertVisible, setMissingAlertVisible] = useState<boolean>(true);

    const [surveySort, setSurveySort] = useState<SortState<SurveySortField> | null>(null);
    const [acuitySort, setAcuitySort] = useState<SortState<AcuitySortField> | null>(null);

    const next = getNextSection(SECTION_ID);

    const reindex = <T extends { index: number }>(arr: T[]): T[] =>
        arr.map((r, i) => ({ ...r, index: i + 1 }));

    /** Fetch CMS surveys for this submission and rebuild the row list
     *  via the adapter. Re-runs on mount and after every mutation
     *  (delete). The endpoint requires either `submission_id` or
     *  `cms_number` (the backend 422s otherwise); we always pass
     *  `submission_id`. */
    const loadSurveyRows = useCallback(async () => {
        if (!detail.id) {
            setSurveyRows([]);
            setSurveyYears(fallbackRecentYears());
            setSurveyLoading(false);
            setSurveyError(null);
            return;
        }
        setSurveyLoading(true);
        setSurveyError(null);
        try {
            // Backend caps `limit` at 200 (422 otherwise). A submission
            // with 200+ surveys would be unusual — at four years × ~50
            // facilities you'd hit it — but if we ever see one, swap
            // this for an offset/limit pagination loop.
            const [response, locations] = await Promise.all([
                listSurveys({ submission_id: detail.id, limit: 200 }),
                listLocations(detail.id),
            ]);
            // Build facility_id → { name, state } from the Schedule locations
            // (SoT) for the Survey columns, and sum the backend Equivalency /
            // sir_adjusted_premium for the footer (null today = data not yet
            // created — obviated; the FE just reads + sums the backend field).
            const refs = new Map<string, SurveyFacilityRef>();
            // Parallel map keyed by cms_number so surveys with a null
            // facility_id (stage data) still resolve a name/state — they
            // group + join by cms_number instead (Henry, jun-19).
            const refsByCms = new Map<string, SurveyFacilityRef>();
            let equivalency = 0;
            let assocPremium = 0;
            for (const loc of locations as SubmissionLocationWire[]) {
                const ref: SurveyFacilityRef = { name: loc.location_name ?? "", state: loc.state ?? "" };
                if (loc.facility_id) refs.set(loc.facility_id, ref);
                const cms = loc.cms_number ?? loc.provider_number;
                if (cms) refsByCms.set(cms, ref);
                // Archived facilities keep their name/state refs (their rows
                // still render, struck through) but drop out of the footer
                // totals — they're excluded from everything pricing-related,
                // and the backend already leaves them out of the premium sums
                // these mirror (OR-1431).
                if (loc.is_excluded === true) continue;
                equivalency += loc.equivalency ?? 0;
                assocPremium += loc.sir_adjusted_premium ?? 0;
            }
            // Columns follow the data: the years the surveys actually span.
            const years = deriveSurveyYears(response.items);
            setSurveyYears(years);
            setSurveyRows(aggregateSurveys(response.items, years, refs, refsByCms));
            setFooterTotals({ equivalency, assocPremium });
        } catch (error) {
            console.error("Failed to load Regulatory surveys", error);
            setSurveyError(error instanceof Error ? error.message : "Failed to load surveys");
            setSurveyRows([]);
            setSurveyYears(fallbackRecentYears());
        } finally {
            setSurveyLoading(false);
        }
    }, [detail.id]);

    useEffect(() => {
        void loadSurveyRows();
    }, [loadSurveyRows]);

    /** Detached copy of the aggregated rows for the Save diff baseline —
     *  the nested census grid is the mutable bit, so clone it per field. */
    const cloneAcuityRows = (rows: AcuityRow[]): AcuityRow[] =>
        rows.map((r) => ({
            ...r,
            census: Object.fromEntries(
                Object.entries(r.census).map(([field, perYear]) => [field, { ...perYear }]),
            ) as AcuityRow["census"],
        }));

    /** Fetch occupancies for this submission and group them into the
     *  Acuity facility rows (full care-type × year census grid). Runs on
     *  mount and after every Save. */
    const loadAcuityRows = useCallback(async () => {
        if (!detail.id) {
            setAcuityRows([]);
            setAcuityBaseline([]);
            setAcuityYears(fallbackRecentYears());
            setAcuityLoading(false);
            setAcuityError(null);
            return;
        }
        setAcuityLoading(true);
        setAcuityError(null);
        // Fresh server truth supersedes anything staged locally.
        setStagedOccupancyDeletes([]);
        try {
            const response = await listOccupancies({ submission_id: detail.id });
            // Columns follow the data (newest first), overlaid with the
            // user's manual order for this submission (localStorage) and
            // any not-yet-persisted manual years from this session.
            const dataYears = deriveAcuityYears(response.items);
            const stillManual = manualYearsRef.current.filter((y) => !dataYears.includes(y));
            const years = applyStoredYearOrder([...dataYears, ...stillManual], readStoredYearOrder(detail.id));
            manualYearsRef.current = stillManual;
            setAcuityYears(years);
            const aggregated = aggregateOccupancies(response.items, years);
            setAcuityRows(aggregated);
            setAcuityBaseline(cloneAcuityRows(aggregated));
            // (Re)arm the Missing Data banner — arriving at the page is one
            // of its trigger points (the other is Save, which reloads).
            setMissingAlertVisible(true);
        } catch (error) {
            console.error("Failed to load Regulatory occupancies", error);
            setAcuityError(error instanceof Error ? error.message : "Failed to load occupancies");
            setAcuityRows([]);
            setAcuityBaseline([]);
            setAcuityYears(fallbackRecentYears());
        } finally {
            setAcuityLoading(false);
        }
    }, [detail.id]);

    useEffect(() => {
        void loadAcuityRows();
    }, [loadAcuityRows]);

    /** Delete a Survey row → DELETE every backing wire id (one facility
     *  row can span multiple wire rows when surveys covered several
     *  years). CMS rows would 403 here, but the trash button is gated
     *  upstream so this loop should never see them in practice. */
    const handleDeleteSurvey = useCallback(
        async (rowId: string) => {
            const row = surveyRows.find((r) => r.id === rowId);
            if (!row || row.readOnly) return;
            try {
                await Promise.all(row.wireIds.map((id) => deleteSurvey(id)));
            } catch (error) {
                console.error("Failed to delete survey row", error);
                setSurveyError(error instanceof Error ? error.message : "Failed to delete survey");
                return;
            }
            await loadSurveyRows();
        },
        [surveyRows, loadSurveyRows],
    );

    // ── Survey Add Row (create) ──────────────────────────────
    const addSurveyRow = useCallback(() => {
        setSaveError(null);
        setShowErrors(false);
        setNewSurveyDrafts((prev) => [...prev, makeNewSurveyDraft()]);
    }, []);

    const updateSurveyDraft = useCallback((id: string, patch: Partial<NewSurveyDraft>) => {
        setNewSurveyDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
    }, []);

    const removeSurveyDraft = useCallback((id: string) => {
        setNewSurveyDrafts((prev) => prev.filter((d) => d.id !== id));
    }, []);

    /** Picking a catalog facility fills the SoT fields (name/state) and links
     *  the survey by `facility_id`. */
    const pickSurveyFacility = useCallback((id: string, facility: CatalogFacility) => {
        setNewSurveyDrafts((prev) =>
            prev.map((d) =>
                d.id === id
                    ? { ...d, facilityId: facility.id, facilityName: facility.name, state: facility.state ?? d.state, catalog: facility }
                    : d,
            ),
        );
    }, []);

    /** Persist user-added Survey rows. Each needs a facility (picked from the
     *  catalog). For a facility that isn't yet a submission location we first
     *  create the location — that's how the survey scopes to this submission
     *  AND propagates to Schedule (SoT). Returns false (and stays put) on a
     *  validation miss or a failed request. */
    const flushNewSurveys = useCallback(async (): Promise<boolean> => {
        if (newSurveyDrafts.length === 0) return true;
        setSaveError(null);
        // Inline validation: facility required; a typed date must be valid.
        const invalid = newSurveyDrafts.some((d) => !d.facilityId || (d.lastSurvey !== "" && formDateToIso(d.lastSurvey) === null));
        if (invalid) {
            setShowErrors(true);
            return false;
        }
        setSavingSurvey(true);
        try {
            const locs = (await listLocations(detail.id)) as SubmissionLocationWire[];
            const createdCatalogIds = new Set<string>();
            for (const d of newSurveyDrafts) {
                const cf = d.catalog;
                if (cf && !createdCatalogIds.has(cf.id) && !locs.some((l) => locationRepresentsCatalog(l, cf))) {
                    await createFacility(detail.id, catalogToFacilityPayload(cf));
                    createdCatalogIds.add(cf.id);
                }
                const payload = draftToSurveyCreate(d);
                if (payload) await createSurvey(payload);
            }
            setNewSurveyDrafts([]);
            setShowErrors(false);
            await loadSurveyRows();
            return true;
        } catch (error) {
            console.error("Failed to create surveys", error);
            setSaveError(error instanceof Error ? error.message : "Failed to create surveys");
            return false;
        } finally {
            setSavingSurvey(false);
        }
    }, [newSurveyDrafts, detail.id, loadSurveyRows]);

    // ── Acuity Add Row (create) ──────────────────────────────
    const addAcuityRow = useCallback(() => {
        setSaveError(null);
        setShowErrors(false);
        setNewAcuityDrafts((prev) => [...prev, makeNewAcuityDraft()]);
    }, []);

    const updateAcuityDraft = useCallback((id: string, patch: Partial<NewAcuityDraft>) => {
        setNewAcuityDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
    }, []);

    const setAcuityDraftValue = useCallback((id: string, year: string, value: string) => {
        setNewAcuityDrafts((prev) =>
            prev.map((d) => (d.id === id ? { ...d, values: { ...d.values, [year]: value } } : d)),
        );
    }, []);

    const removeAcuityDraft = useCallback((id: string) => {
        setNewAcuityDrafts((prev) => prev.filter((d) => d.id !== id));
    }, []);

    const pickAcuityFacility = useCallback((id: string, facility: CatalogFacility) => {
        setNewAcuityDrafts((prev) =>
            prev.map((d) =>
                d.id === id
                    ? { ...d, facilityId: facility.id, facilityName: facility.name, state: facility.state ?? d.state, catalog: facility }
                    : d,
            ),
        );
    }, []);

    // ── Acuity in-place edit (existing rows) ─────────────────
    /** Stage an edit to a loaded Acuity cell (one care-type sub-row × year)
     *  — updates the working census string; persisted on Save by diffing
     *  against the baseline. */
    const setAcuityCensus = useCallback(
        (rowId: string, field: AcuityBedField, year: string, value: string) => {
            setAcuityRows((prev) =>
                prev.map((r) => {
                    if (r.id !== rowId) return r;
                    // Archived facilities are frozen (OR-1431) — the same
                    // guarantee `useFacilities.update` enforces for Schedule's
                    // own fields. Enforced here, not just via the row's
                    // cosmetic dimming, so no keyboard path can slip a census
                    // edit through for an archived facility.
                    if (isRowArchived(r)) return r;
                    return { ...r, census: { ...r.census, [field]: { ...r.census[field], [year]: value } } };
                }),
            );
        },
        [isRowArchived],
    );

    /** Persist edits to ALREADY-LOADED Acuity rows. Each changed (facility,
     *  year) cell maps to one occupancy record: collect the changed
     *  care-type bed fields per (facility, year), then PATCH the existing
     *  record or CREATE one when that year had none. (Brand-new facilities
     *  still go through the Add-Row draft flow.) */
    const flushAcuityEdits = useCallback(async (): Promise<boolean> => {
        const baseById = new Map(acuityBaseline.map((r) => [r.id, r]));
        // key = `${facilityKey}|${year}` → the occupancy create/patch payload.
        const pending = new Map<string, { facilityId: string; year: number; occId: string; fields: Record<string, number | null> }>();
        for (const row of acuityRows) {
            const base = baseById.get(row.id);
            if (!base) continue; // not a loaded row (shouldn't happen)
            // A row can be archived AFTER an edit was staged (e.g. via this
            // page's own Archive action) but before Save — discard any
            // pending census changes for it rather than persist them, same
            // freeze `setAcuityCensus` now enforces for new edits.
            if (isRowArchived(row)) continue;
            for (const careType of ACUITY_CARE_TYPES) {
                const perYear = row.census[careType.field] ?? {};
                for (const [year, val] of Object.entries(perYear)) {
                    if ((base.census[careType.field]?.[year] ?? "") === (val ?? "")) continue; // unchanged
                    const pkey = `${row.facilityKey}|${year}`;
                    let p = pending.get(pkey);
                    if (!p) {
                        p = { facilityId: row.facilityId, year: Number(year), occId: row.occupancyByYear[year] ?? "", fields: {} };
                        pending.set(pkey, p);
                    }
                    p.fields[careType.field] = toIntOrNull(val);
                }
            }
        }
        if (pending.size === 0) return true;
        try {
            for (const p of pending.values()) {
                if (p.occId) {
                    await patchOccupancy(p.occId, p.fields as OccupancyUpdateWire);
                } else if (p.facilityId) {
                    await createOccupancy({ facility_id: p.facilityId, year: p.year, ...p.fields } as OccupancyCreateWire);
                }
                // else: unlinked facility + no record → can't persist; skip.
            }
            await loadAcuityRows();
            return true;
        } catch (error) {
            console.error("Failed to save Acuity edits", error);
            setSaveError(friendlyAcuitySaveError(error instanceof Error ? error.message : "Failed to save occupancies"));
            return false;
        }
    }, [acuityRows, acuityBaseline, loadAcuityRows, isRowArchived]);

    /** Persist user-added Acuity rows. Each needs a facility, a care Type and
     *  ≥1 year value. New facilities get a SubmissionLocation first (links +
     *  propagates). Writes are MERGE-AWARE (OR-1152): the backend keeps one
     *  occupancy per (facility, year), so a year that already has a record
     *  gets its care-type column PATCHed — only truly new (facility, year)
     *  pairs are POSTed. The old POST-per-year loop either 409'd mid-batch
     *  (persisting the earlier years — QA's "partial write") or inserted
     *  silent duplicates where the DB constraint isn't applied. */
    const flushNewOccupancies = useCallback(async (): Promise<boolean> => {
        if (newAcuityDrafts.length === 0) return true;
        setSaveError(null);
        // Inline validation: facility + Type required; ≥1 year value.
        const invalid = newAcuityDrafts.some(
            (d) => !d.facilityId || !d.type || draftToOccupancyWrites(d, EMPTY_OCC_INDEX).creates.length === 0,
        );
        if (invalid) {
            setShowErrors(true);
            return false;
        }
        // Provisional guard (OR-1417): block Add Row for a facility already in
        // the table so its data doesn't silently merge into the existing row.
        // The row shows the "already in the table" warning; pending team review
        // of the intended behavior.
        const duplicatesExisting = newAcuityDrafts.some(
            (d) => d.catalog && acuityRows.some((r) => acuityRowMatchesCatalog(r, d.catalog!)),
        );
        if (duplicatesExisting) {
            setShowErrors(true);
            return false;
        }
        setSavingSurvey(true);
        try {
            const locs = (await listLocations(detail.id)) as SubmissionLocationWire[];
            const createdCatalogIds = new Set<string>();
            // Fresh (facility, year) → occupancy-id index so each draft year
            // resolves to PATCH-existing vs POST-new against CURRENT data.
            const occIndex = occupancyIdByFacilityYear((await listOccupancies({ submission_id: detail.id })).items);
            for (const d of newAcuityDrafts) {
                const cf = d.catalog;
                if (cf && !createdCatalogIds.has(cf.id) && !locs.some((l) => locationRepresentsCatalog(l, cf))) {
                    await createFacility(detail.id, catalogToFacilityPayload(cf));
                    createdCatalogIds.add(cf.id);
                }
                const writes = draftToOccupancyWrites(d, occIndex);
                for (const p of writes.patches) await patchOccupancy(p.occupancyId, p.payload);
                for (const c of writes.creates) {
                    const created = await createOccupancy(c);
                    // Register the new record: a later draft in this same save
                    // targeting the same (facility, year) must PATCH it.
                    if (created?.id && c.facility_id) occIndex.set(occupancyKey(c.facility_id, c.year), created.id);
                }
            }
            setNewAcuityDrafts([]);
            setShowErrors(false);
            await loadAcuityRows();
            return true;
        } catch (error) {
            console.error("Failed to save new Acuity rows", error);
            setSaveError(friendlyAcuitySaveError(error instanceof Error ? error.message : "Failed to save Acuity rows"));
            // Reload so the table reflects whatever DID persist — never leave
            // the user looking at a state that only a hard refresh reveals.
            await loadAcuityRows();
            return false;
        } finally {
            setSavingSurvey(false);
        }
    }, [newAcuityDrafts, acuityRows, detail.id, loadAcuityRows]);

    const sortedSurvey = useMemo(() => {
        if (!surveySort) return surveyRows;
        const arr = [...surveyRows];
        arr.sort((a, b) => {
            const cmp = compareSurvey(a, b, surveySort.field);
            return surveySort.dir === "asc" ? cmp : -cmp;
        });
        return arr;
    }, [surveyRows, surveySort]);

    const sortedAcuity = useMemo(() => {
        if (!acuitySort) return acuityRows;
        const arr = [...acuityRows];
        arr.sort((a, b) => {
            const cmp = compareAcuity(a, b, acuitySort.field);
            return acuitySort.dir === "asc" ? cmp : -cmp;
        });
        return arr;
    }, [acuityRows, acuitySort]);

    /** Delete a loaded Acuity row (OR-1298). Removes THIS row's occupancy
     *  records and nothing else: a facility is a one-to-many relation over
     *  occupancies, and archiving or deleting it belongs to Schedule, which
     *  owns facilities. */
    const handleDeleteAcuity = (rowId: string) => {
        const row = acuityRows.find((r) => r.id === rowId);
        if (!row) return;
        // `wireIds` keeps EVERY backing record behind the row, including
        // duplicated years (OR-1152). Deletes flush on Save, like everything
        // else staged here.
        if (row.facilityId !== "") {
            setStagedOccupancyDeletes((prev) => [...new Set([...prev, ...row.wireIds])]);
        }
        setAcuityRows((prev) => reindex(prev.filter((r) => r.id !== rowId)));
    };

    // ── Acuity year columns (OR-1328) ────────────────────────
    /** Apply + persist a new column order for this submission. */
    const commitYearOrder = useCallback(
        (years: string[]) => {
            setAcuityYears(years);
            storeYearOrder(detail.id, years);
        },
        [detail.id],
    );

    /** Open the "new year column" header input. Inserts at the right edge
     *  by default, or adjacent to `nearYear` (Column Left / Column Right). */
    const openYearDraft = useCallback(
        (nearYear?: string, side?: "left" | "right") => {
            setYearDraftError(null);
            let insertAt = acuityYears.length;
            if (nearYear) {
                const i = acuityYears.indexOf(nearYear);
                if (i !== -1) insertAt = side === "left" ? i : i + 1;
            }
            setYearDraft({ value: "", insertAt });
        },
        [acuityYears],
    );

    const changeYearDraft = useCallback((value: string) => {
        setYearDraftError(null);
        setYearDraft((prev) => (prev ? { ...prev, value: value.replace(/\D/g, "").slice(0, 4) } : prev));
    }, []);

    const cancelYearDraft = useCallback(() => {
        setYearDraft(null);
        setYearDraftError(null);
    }, []);

    /** Confirm the typed year (Enter / blur). Duplicates are BLOCKED per
     *  design — the input stays put in an error state so the user can fix
     *  it. An empty value on blur reverts the column creation. */
    const commitYearDraft = useCallback(() => {
        if (!yearDraft) return;
        if (yearDraft.value === "") {
            // Empty on blur → revert the column creation.
            setYearDraft(null);
            setYearDraftError(null);
            return;
        }
        if (!isValidYearLabel(yearDraft.value)) {
            setYearDraftError("Enter a 4-digit year between 2000 and 2100.");
            return;
        }
        if (acuityYears.includes(yearDraft.value)) {
            setYearDraftError("This year has already been added");
            return;
        }
        manualYearsRef.current = [...manualYearsRef.current, yearDraft.value];
        const next = [...acuityYears];
        next.splice(Math.min(yearDraft.insertAt, next.length), 0, yearDraft.value);
        commitYearOrder(next);
        setYearDraft(null);
        setYearDraftError(null);
    }, [yearDraft, acuityYears, commitYearOrder]);

    /** Move a year column one slot left/right (header context menu). */
    const moveYearColumn = useCallback(
        (year: string, dir: -1 | 1) => {
            const i = acuityYears.indexOf(year);
            const j = i + dir;
            if (i === -1 || j < 0 || j >= acuityYears.length) return;
            const next = [...acuityYears];
            [next[i], next[j]] = [next[j], next[i]];
            commitYearOrder(next);
        },
        [acuityYears, commitYearOrder],
    );

    /** Drag-and-drop reorder: move `source` into `target`'s slot. */
    const reorderYearColumn = useCallback(
        (source: string, target: string) => {
            if (source === target) return;
            const from = acuityYears.indexOf(source);
            const to = acuityYears.indexOf(target);
            if (from === -1 || to === -1) return;
            const next = [...acuityYears];
            next.splice(from, 1);
            next.splice(to, 0, source);
            commitYearOrder(next);
        },
        [acuityYears, commitYearOrder],
    );

    /** Delete a year column: stage EVERY backing record of that year for
     *  deletion (flushed on Save, incl. duplicated-year records — OR-1152)
     *  and strip the year from the rows, the baseline and the column list. */
    const deleteYearColumn = useCallback(
        (year: string) => {
            const stripYear = (rows: AcuityRow[]): AcuityRow[] =>
                rows.map((r) => {
                    const census = Object.fromEntries(
                        Object.entries(r.census).map(([field, perYear]) => {
                            const { [year]: _dropped, ...rest } = perYear;
                            return [field, rest];
                        }),
                    ) as AcuityRow["census"];
                    const { [year]: _id, ...occupancyByYear } = r.occupancyByYear;
                    const { [year]: _ids, ...occupancyIdsByYear } = r.occupancyIdsByYear;
                    return { ...r, census, occupancyByYear, occupancyIdsByYear };
                });
            const staged = acuityRows.flatMap((r) => r.occupancyIdsByYear[year] ?? []);
            if (staged.length > 0) setStagedOccupancyDeletes((prev) => [...new Set([...prev, ...staged])]);
            setAcuityRows(stripYear);
            setAcuityBaseline(stripYear);
            manualYearsRef.current = manualYearsRef.current.filter((y) => y !== year);
            commitYearOrder(acuityYears.filter((y) => y !== year));
        },
        [acuityRows, acuityYears, commitYearOrder],
    );

    // Facilities with rendered years that have no data at all — feeds the
    // Missing Data banner + the per-facility warning icon (OR-1328).
    const acuityMissing = useMemo(() => missingYearsByFacility(acuityRows, acuityYears), [acuityRows, acuityYears]);

    // Save persists user-added Survey + Acuity rows AND in-place edits to
    // loaded Acuity rows, plus any staged facility deletions (Acuity trash),
    // without navigating. Continue advances (the footer guards against leaving
    // while dirty).
    const onSave = async () => {
        const hadOccupancyDeletes = stagedOccupancyDeletes.length > 0;
        setSaveError(null);
        // The saving lock covers the WHOLE flush — including the staged
        // DELETE batch below — so a double-click can't re-enter and fire
        // duplicate deletes.
        setSavingSurvey(true);
        try {
            // Flush staged occupancy deletions first, while the records are
            // still reachable — they belong to facilities whose location is
            // about to be un-linked. Without this the records survive as
            // orphans and re-adding the facility resurrects them (Cavero's
            // delete→add repro, OR-1152).
            if (hadOccupancyDeletes) {
                const staged = stagedOccupancyDeletes;
                const results = await Promise.allSettled(staged.map((id) => deleteOccupancy(id)));
                // A 404 means the record is already gone — that's the outcome
                // we wanted, so only genuine failures stay staged for retry.
                const failedIds = staged.filter((_, i) => {
                    const r = results[i];
                    if (r.status !== "rejected") return false;
                    const msg = r.reason instanceof Error ? r.reason.message : String(r.reason);
                    return !/not found|404/i.test(msg);
                });
                if (failedIds.length > 0) {
                    setSaveError("Failed to delete the facility's Acuity records");
                    // Re-sync the rows with the backend, then re-stage the
                    // survivors so the next Save retries them BEFORE the
                    // location un-link can strand them as orphans.
                    await loadAcuityRows();
                    setStagedOccupancyDeletes(failedIds);
                    return;
                }
                setStagedOccupancyDeletes([]);
            }
            // Flush staged facility removals next (Acuity delete → un-link
            // the SubmissionLocation). Propagates to Schedule via the shared
            // SoT.
            if (facilitiesDirty) {
                const facOk = await saveFacilities();
                if (!facOk) {
                    setSaveError("Failed to save facility changes");
                    return;
                }
            }
            const surveysOk = await flushNewSurveys();
            if (!surveysOk) return;
            const occOk = await flushNewOccupancies();
            if (!occOk) return;
            const editsOk = await flushAcuityEdits();
            if (!editsOk) return;
            // A removed facility must drop from both tabs — re-scope from the
            // backend (surveys/occupancies query by the submission's locations).
            if (facilitiesDirty || hadOccupancyDeletes) {
                await loadSurveyRows();
                await loadAcuityRows();
            }
            // Save is the other Missing Data trigger point (besides page
            // load) — re-arm the banner even when nothing needed flushing.
            setMissingAlertVisible(true);
        } finally {
            setSavingSurvey(false);
        }
    };
    const onContinue = () => navigate(next ? `/submissions/${detail.id}/${next.path}` : "/submissions");
    // Loaded Acuity rows whose census was edited vs the last-loaded baseline.
    const acuityDirty = useMemo(() => {
        const baseById = new Map(acuityBaseline.map((r) => [r.id, r]));
        return acuityRows.some((r) => {
            const b = baseById.get(r.id);
            if (!b) return false;
            return ACUITY_CARE_TYPES.some(({ field }) =>
                Object.keys(r.census[field] ?? {}).some(
                    (y) => (r.census[field]?.[y] ?? "") !== (b.census[field]?.[y] ?? ""),
                ),
            );
        });
    }, [acuityRows, acuityBaseline]);
    const isRegulatoryDirty =
        newSurveyDrafts.length > 0 || newAcuityDrafts.length > 0 || acuityDirty || facilitiesDirty || stagedOccupancyDeletes.length > 0;
    // Block Save while an Acuity draft points at a facility already in the
    // table — otherwise its data would silently merge into the existing row
    // (OR-1417). The draft row shows the inline "already in the table" warning.
    const acuityDraftDuplicatesExisting = useMemo(
        () => newAcuityDrafts.some((d) => d.catalog && acuityRows.some((r) => acuityRowMatchesCatalog(r, d.catalog!))),
        [newAcuityDrafts, acuityRows],
    );

    const renewal = detail.accountBasics.renewalDate || "";
    const expiration = detail.accountBasics.expirationDate || "";

    return (
        <div className="flex flex-1 flex-col lg:min-h-0">
            <div className="flex flex-col gap-6 lg:min-h-0 lg:flex-1 lg:pb-4">
            <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="flex flex-col gap-1">
                    <h2 className="text-primary text-md font-semibold">Regulatory</h2>
                    <p className="text-tertiary text-sm">Survey data, citations and complaints</p>
                </div>
                <div className="flex w-full items-stretch justify-between gap-6 md:w-auto md:shrink-0 md:justify-start">
                    <DateField label="Renewal Date" value={renewal} />
                    <span aria-hidden="true" className="bg-border-secondary hidden w-px self-stretch md:block" />
                    <DateField label="Expiration Date" value={expiration} />
                </div>
            </header>

            <div className="border-secondary bg-primary flex flex-col overflow-hidden rounded-lg border lg:min-h-0 lg:flex-1">
                <div className="border-secondary flex shrink-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
                    <Tabs
                        selectedKey={activeTab}
                        onSelectionChange={(k) => setActiveTab(k as RegulatoryTab)}
                        className="w-max"
                        aria-label="Regulatory views"
                    >
                        <Tabs.List type="button-minimal" size="sm">
                            {REGULATORY_TABS.map((tab) => (
                                <Tabs.Item key={tab.id} id={tab.id} label={tab.label} data-autotest={tab.autotest} />
                            ))}
                        </Tabs.List>
                    </Tabs>
                    {/* Acuity: "+ Add" opens a Row | Column dropdown (OR-1328
                      * — Column appends a new year column whose header is a
                      * focused input). Survey keeps the plain Add Row until
                      * its own column redesign lands (separate ticket). */}
                    {activeTab === "acuity" ? (
                        <AriaMenuTrigger>
                            {/* Same atlas link-gray look as the Survey "Add
                              * Row" button — atlas Button wraps the RAC
                              * Button, so it works as the MenuTrigger's
                              * pressable directly. */}
                            {/* Literal "+" in the label — same treatment as
                              * Operational's "+ Add Row", so every section's
                              * Add control reads identically. px/py give the
                              * focus ring breathing room (link buttons hug
                              * their content). */}
                            <Button color="link-gray" size="sm" className="rounded-md px-2 py-1" data-autotest="regulatory-acuity-tab-add-button">
                                + Add
                            </Button>
                            <AriaPopover placement="bottom end" offset={4} className="z-50 entering:animate-in entering:fade-in entering:zoom-in-95">
                                <AriaMenu
                                    aria-label="Add to Acuity table"
                                    className="bg-primary border-secondary min-w-40 rounded-lg border p-1 shadow-lg outline-none"
                                    onAction={(key) => {
                                        if (key === "row") addAcuityRow();
                                        else openYearDraft();
                                    }}
                                >
                                    <AriaMenuItem
                                        id="row"
                                        className="text-secondary hover:bg-secondary_hover focus:bg-secondary_hover flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm font-semibold outline-none"
                                        data-autotest="regulatory-acuity-tab-add-row"
                                    >
                                        <Rows01 aria-hidden="true" className="text-fg-quaternary size-4" />
                                        Row
                                    </AriaMenuItem>
                                    <AriaMenuItem
                                        id="column"
                                        className="text-secondary hover:bg-secondary_hover focus:bg-secondary_hover flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm font-semibold outline-none"
                                        data-autotest="regulatory-acuity-tab-add-column"
                                    >
                                        <Columns01 aria-hidden="true" className="text-fg-quaternary size-4" />
                                        Column
                                    </AriaMenuItem>
                                </AriaMenu>
                            </AriaPopover>
                        </AriaMenuTrigger>
                    ) : (
                        <Button color="link-gray" size="sm" onClick={addSurveyRow} data-autotest="regulatory-survey-tab-add-row-button">
                            + Add Row
                        </Button>
                    )}
                </div>

                {/* Missing Data Detected — triggered on page load and on Save
                  * (OR-1328); dismissible; only while gaps actually exist. */}
                {activeTab === "acuity" && missingAlertVisible && acuityMissing.size > 0 && !acuityLoading && (
                    <MissingDataBanner
                        description="Check the flagged facilities below and contact the broker to request the missing years."
                        onClose={() => setMissingAlertVisible(false)}
                    />
                )}

                {saveError && (
                    <div role="alert" className="border-error-secondary bg-error-primary text-error-primary border-b px-6 py-3 text-sm">
                        {saveError}
                    </div>
                )}

                {activeTab === "survey" && surveyError && (
                    <div
                        role="alert"
                        className="border-error-secondary bg-error-primary text-error-primary border-b px-6 py-3 text-sm"
                    >
                        Couldn't load surveys: {surveyError}
                    </div>
                )}
                {activeTab === "survey" && surveyLoading && (
                    <div className="text-tertiary px-6 py-8 text-sm">Loading surveys…</div>
                )}
                {activeTab === "survey" && !surveyLoading && !surveyError && surveyRows.length === 0 && newSurveyDrafts.length === 0 && (
                    <TableEmptyState
                        icon={Building07}
                        title="Add a Survey"
                        description="Use the Add Row button above to start building the Survey"
                    />
                )}
                {activeTab === "survey" && !surveyLoading && (surveyRows.length > 0 || newSurveyDrafts.length > 0) && (
                    <SurveyTable
                        rows={sortedSurvey}
                        drafts={newSurveyDrafts}
                        years={surveyYears}
                        footerTotals={footerTotals}
                        sort={surveySort}
                        onSort={(field) =>
                            setSurveySort((current) => {
                                if (!current || current.field !== field) return { field, dir: "asc" };
                                if (current.dir === "asc") return { field, dir: "desc" };
                                return null;
                            })
                        }
                        onDelete={handleDeleteSurvey}
                        isRowArchived={isRowArchived}
                        getFacilityForRow={getFacilityForRow}
                        onArchiveFacility={setFacilityArchived}
                        onDraftChange={updateSurveyDraft}
                        onDraftRemove={removeSurveyDraft}
                        onDraftPickFacility={pickSurveyFacility}
                        showErrors={showErrors}
                    />
                )}

                {activeTab === "acuity" && acuityError && (
                    <div
                        role="alert"
                        className="border-error-secondary bg-error-primary text-error-primary border-b px-6 py-3 text-sm"
                    >
                        Couldn't load occupancies: {acuityError}
                    </div>
                )}
                {activeTab === "acuity" && acuityLoading && (
                    <div className="text-tertiary px-6 py-8 text-sm">Loading occupancies…</div>
                )}
                {activeTab === "acuity" && !acuityLoading && !acuityError && acuityRows.length === 0 && newAcuityDrafts.length === 0 && yearDraft === null && (
                    <TableEmptyState
                        icon={Building07}
                        title="No occupancy data"
                        description="Use the + Add button above to add occupancy data for this submission."
                    />
                )}
                {activeTab === "acuity" && !acuityLoading && (acuityRows.length > 0 || newAcuityDrafts.length > 0 || yearDraft !== null) && (
                    <AcuityTable
                        rows={sortedAcuity}
                        drafts={newAcuityDrafts}
                        years={acuityYears}
                        yearDraft={yearDraft}
                        yearDraftError={yearDraftError}
                        missing={acuityMissing}
                        sort={acuitySort}
                        onSort={(field) =>
                            setAcuitySort((current) => {
                                if (!current || current.field !== field) return { field, dir: "asc" };
                                if (current.dir === "asc") return { field, dir: "desc" };
                                return null;
                            })
                        }
                        onDelete={handleDeleteAcuity}
                        isRowArchived={isRowArchived}
                        getFacilityForRow={getFacilityForRow}
                        onArchiveFacility={setFacilityArchived}
                        onCensusChange={setAcuityCensus}
                        onDraftChange={updateAcuityDraft}
                        onDraftValue={setAcuityDraftValue}
                        onDraftRemove={removeAcuityDraft}
                        onDraftPickFacility={pickAcuityFacility}
                        onYearDraftChange={changeYearDraft}
                        onYearDraftCommit={commitYearDraft}
                        onYearDraftCancel={cancelYearDraft}
                        onMoveYear={moveYearColumn}
                        onAddYearAt={openYearDraft}
                        onDeleteYear={deleteYearColumn}
                        onReorderYear={reorderYearColumn}
                        showErrors={showErrors}
                    />
                )}
            </div>
            </div>

            <SubmissionFooter
                onSave={onSave}
                onContinue={onContinue}
                isDirty={isRegulatoryDirty}
                isSaving={savingSurvey}
                nextSectionLabel={next?.label ?? null}
                saveBlockedReason={
                    acuityDraftDuplicatesExisting
                        ? "This facility is already in the Acuity table — edit its row instead of adding it again."
                        : null
                }
            />
        </div>
    );
}

// ─── Survey table ───────────────────────────────────────────────────────────

interface SurveyTableProps {
    rows: SurveyRow[];
    drafts: NewSurveyDraft[];
    /** Year column groups to render, derived from the survey data. */
    years: string[];
    footerTotals: SurveyFooterTotals;
    sort: SortState<SurveySortField> | null;
    onSort: (field: SurveySortField) => void;
    onDelete: (id: string) => void;
    /** Archive state + toggles for the row's underlying facility (OR-1431). */
    isRowArchived: (row: { facilityId: string; facilityName: string }) => boolean;
    /** Resolves the facility behind a row, if any — undefined rows get Delete
     *  only, since there's nothing to archive. */
    getFacilityForRow: (row: { facilityId: string; facilityName: string }) => Facility | undefined;
    onArchiveFacility: (facilityId: string, isExcluded: boolean) => void;
    onDraftChange: (id: string, patch: Partial<NewSurveyDraft>) => void;
    onDraftRemove: (id: string) => void;
    onDraftPickFacility: (id: string, facility: CatalogFacility) => void;
    showErrors: boolean;
}

/** Equivalency — up to 2 decimals, no trailing zeros (matches Schedule). */
function formatEquivalency(n: number): string {
    return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** Assoc. Premium — whole-dollar USD. */
function formatPremium(n: number): string {
    return `$${Math.round(n).toLocaleString("en-US")}`;
}

function SurveyTable({
    rows,
    drafts,
    years,
    footerTotals,
    sort,
    onSort,
    onDelete,
    isRowArchived,
    getFacilityForRow,
    onArchiveFacility,
    onDraftChange,
    onDraftRemove,
    onDraftPickFacility,
    showErrors,
}: SurveyTableProps) {
    return (
        <div className="atlas-scrollbar overflow-auto lg:min-h-0 lg:flex-1">
            <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
                <thead>
                    {/* Row 1: column-group labels. White background +
                      * `text-primary` 14/700 (per the Figma spec). The
                      * label itself lives in a `sticky left-0` span so
                      * it sticks to the left edge of the scroll
                      * container as the user drags horizontally — when
                      * the cell scrolls past, sticky clamps to the
                      * cell's right edge and the next group's label
                      * naturally rolls into the leftmost slot. */}
                    <tr>
                        <th
                            colSpan={COST_COLUMNS.length}
                            className="bg-primary border-secondary border-b p-0 text-left"
                        >
                            {/* "Survey Costs" is the first group and its cell
                              * starts at x=0, so it doesn't need sticky-left
                              * — the title anchors to the natural left of
                              * its cell. (Year groups further right still
                              * use STICKY_SECTION_OFFSET so they remain
                              * visible during horizontal scroll.) */}
                            <span className="text-primary inline-block px-6 py-2.5 text-sm font-bold" data-autotest="regulatory-survey-tab-survey-costs-section-label">
                                Survey Costs
                            </span>
                        </th>
                        {years.map((year) => (
                            <th
                                key={year}
                                colSpan={YEAR_COLUMNS.length}
                                className="bg-primary border-secondary border-b border-l p-0 text-left"
                            >
                                <span className={cx("text-primary md:sticky inline-block px-6 py-2.5 text-sm font-bold", STICKY_SECTION_OFFSET)}>
                                    {year}
                                </span>
                            </th>
                        ))}
                        <th
                            className="bg-primary border-secondary border-b border-l md:sticky md:right-0 md:z-20"
                            style={{ width: TRASH_COL_W, minWidth: TRASH_COL_W }}
                            aria-hidden="true"
                        />
                    </tr>

                    {/* Row 2: column titles with sort icons. The `flag`
                      * column is informational only — render an empty
                      * sticky placeholder instead of a SortHeader. */}
                    <tr className="bg-secondary">
                        {COST_COLUMNS.map((col) => (
                            <th
                                key={col.id}
                                scope="col"
                                className={cx(
                                    "border-secondary border-b px-6 py-2 text-left",
                                    col.sticky && STICKY_HEAD[col.sticky],
                                )}
                                style={{ width: col.width, minWidth: col.width }}
                            >
                                <SortHeader field={col.id} sort={sort} onSortChange={onSort}>
                                    {col.label}
                                </SortHeader>
                                {col.sticky === "left-80" && <StickyColDivider mdOnly />}
                            </th>
                        ))}
                        {years.map((year, yIdx) =>
                            YEAR_COLUMNS.map((col, cIdx) => {
                                const sortField = `${year}.${col.id}` as SurveySortField;
                                const isFirstInGroup = cIdx === 0;
                                return (
                                    <th
                                        key={`${year}.${col.id}`}
                                        scope="col"
                                        className={cx(
                                            "border-secondary border-b px-6 py-2 text-left",
                                            isFirstInGroup && "border-l",
                                            yIdx === 0 && cIdx === 0 && "border-l",
                                        )}
                                        style={{ width: col.width, minWidth: col.width }}
                                    >
                                        <SortHeader field={sortField} sort={sort} onSortChange={onSort}>
                                            {col.label}
                                        </SortHeader>
                                    </th>
                                );
                            }),
                        )}
                        <th
                            className={cx(STICKY_RIGHT_HEAD, "border-secondary border-b border-l")}
                            style={{ width: TRASH_COL_W, minWidth: TRASH_COL_W }}
                            aria-hidden="true"
                        />
                    </tr>
                </thead>

                <tbody>
                    {rows.map((row) => {
                        const archived = isRowArchived(row);
                        const facilityForRow = getFacilityForRow(row);
                        return (
                        <tr key={row.id} className={cx("text-tertiary", archived && ARCHIVED_ROW)}>
                            <td
                                className={cx(STICKY_CELL["left-0"], "border-secondary border-b px-6 py-4")}
                                style={{ width: COST_COLUMNS[0].width, minWidth: COST_COLUMNS[0].width }}
                            >
                                {row.index}
                            </td>
                            <td
                                className={cx(STICKY_CELL["left-80"], "border-secondary border-b px-6 py-4")}
                                style={{ width: COST_COLUMNS[1].width, minWidth: COST_COLUMNS[1].width }}
                            >
                                <div
                                    className="max-h-20 overflow-y-auto whitespace-normal [overflow-wrap:anywhere] leading-snug"
                                    data-autotest="regulatory-survey-tab-facility-name-cell"
                                >
                                    {row.facilityName || "—"}
                                </div>
                                <StickyColDivider mdOnly />
                            </td>
                            <td
                                className="border-secondary border-b px-6 py-4"
                                style={{ width: COST_COLUMNS[2].width, minWidth: COST_COLUMNS[2].width }}
                                data-autotest="regulatory-survey-tab-state-cell"
                            >
                                {row.state || "—"}
                            </td>
                            <td
                                className="border-secondary border-b px-6 py-4 text-right tabular-nums"
                                style={{ width: COST_COLUMNS[3].width, minWidth: COST_COLUMNS[3].width }}
                                data-autotest="regulatory-survey-tab-fine-count-cell"
                            >
                                {row.fineCount}
                            </td>
                            <td
                                className="border-secondary border-b px-6 py-4 text-right tabular-nums"
                                style={{ width: COST_COLUMNS[4].width, minWidth: COST_COLUMNS[4].width }}
                                data-autotest="regulatory-survey-tab-total-fines-cell"
                            >
                                {row.totalFines || "—"}
                            </td>

                            {years.map((year, yIdx) =>
                                YEAR_COLUMNS.map((col, cIdx) => {
                                    const value = row.years[year][col.id];
                                    const isFirstInGroup = cIdx === 0;
                                    const numeric = typeof value === "number";
                                    return (
                                        <td
                                            key={`${row.id}.${year}.${col.id}`}
                                            className={cx(
                                                "border-secondary border-b px-6 py-4",
                                                isFirstInGroup && "border-l",
                                                yIdx === 0 && cIdx === 0 && "border-l",
                                                numeric && "text-right tabular-nums",
                                            )}
                                            style={{ width: col.width, minWidth: col.width }}
                                            data-autotest={`regulatory-survey-tab-${camelToKebab(col.id)}-${year}-cell`}
                                        >
                                            {/* null/empty string → "—"; a numeric 0 stays "0". */}
                                            {numeric ? value : value || "—"}
                                        </td>
                                    );
                                }),
                            )}

                            <td
                                className={cx(STICKY_RIGHT_CELL, "border-secondary border-b border-l px-3 py-4")}
                                style={{ width: TRASH_COL_W, minWidth: TRASH_COL_W }}
                            >
                                {/* CMS-sourced surveys are read-only (the backend
                                  * 403s PATCH/DELETE of the survey record), so
                                  * Delete stays disabled with an explanatory hint.
                                  * Facility Archive/Restore is a different resource
                                  * (the submission's location, not the CMS survey)
                                  * and isn't blocked by that 403, so it still works
                                  * on these rows. */}
                                <FacilityRowActionsMenu
                                    facilityName={row.facilityName}
                                    isArchived={archived}
                                    canArchive={facilityForRow !== undefined}
                                    onArchive={
                                        facilityForRow ? () => onArchiveFacility(facilityForRow.id, true) : undefined
                                    }
                                    onRestore={
                                        facilityForRow ? () => onArchiveFacility(facilityForRow.id, false) : undefined
                                    }
                                    onDelete={row.readOnly ? undefined : () => onDelete(row.id)}
                                    deleteTitle="Delete survey"
                                    deleteDescription="Delete this facility's survey records from the submission. This action cannot be undone."
                                    deleteDisabledHint={row.readOnly ? "CMS-sourced surveys are read-only" : undefined}
                                    data-autotest="regulatory-survey-tab-row-actions-trigger"
                                />
                            </td>
                        </tr>
                        );
                    })}

                    {/* User-added editable rows (not yet saved). Rendered below
                      * the loaded rows; POSTed on Save & Continue. */}
                    {drafts.map((draft, i) => (
                        <SurveyDraftRow
                            key={draft.id}
                            draft={draft}
                            index={rows.length + i + 1}
                            years={years}
                            showErrors={showErrors}
                            onChange={onDraftChange}
                            onRemove={onDraftRemove}
                            onPickFacility={onDraftPickFacility}
                        />
                    ))}
                </tbody>

                {/* Per-year footer — Equivalency + Assoc. Premium read from
                  * the backend location columns (equivalency /
                  * sir_adjusted_premium) summed across facilities, repeated
                  * under each year group per Andressa's Figma. */}
                <tfoot>
                    <tr className="bg-secondary font-semibold">
                        <td
                            className="border-secondary md:sticky md:left-0 md:z-10 border-b bg-secondary px-6 py-3"
                            style={{ width: COST_COLUMNS[0].width, minWidth: COST_COLUMNS[0].width }}
                        />
                        <td
                            className="border-secondary md:sticky md:left-[80px] md:z-10 border-b bg-secondary px-6 py-3"
                            style={{ width: COST_COLUMNS[1].width, minWidth: COST_COLUMNS[1].width }}
                        >
                            <StickyColDivider mdOnly />
                        </td>
                        <td className="border-secondary border-b" style={{ width: COST_COLUMNS[2].width, minWidth: COST_COLUMNS[2].width }} />
                        <td className="border-secondary border-b" style={{ width: COST_COLUMNS[3].width, minWidth: COST_COLUMNS[3].width }} />
                        <td className="border-secondary border-b" style={{ width: COST_COLUMNS[4].width, minWidth: COST_COLUMNS[4].width }} />

                        {years.map((year) => (
                            <Fragment key={year}>
                                <td
                                    className="border-secondary border-b border-l px-6 py-3 text-sm whitespace-nowrap"
                                    style={{ width: YEAR_COLUMNS[0].width, minWidth: YEAR_COLUMNS[0].width }}
                                >
                                    <span className="text-quaternary">
                                        Equivalency:{" "}
                                    </span>
                                    <span className="text-primary tabular-nums" data-autotest={`regulatory-survey-tab-equivalency-${year}-total`}>
                                        {formatEquivalency(footerTotals.equivalency)}
                                    </span>
                                </td>
                                <td
                                    className="border-secondary border-b px-6 py-3 text-sm whitespace-nowrap"
                                    colSpan={YEAR_COLUMNS.length - 1}
                                >
                                    <span className="text-quaternary" >
                                        Assoc. Premium:{" "}
                                    </span>
                                    <span className="text-primary tabular-nums" data-autotest={`regulatory-survey-tab-assoc-premium-${year}-total`}>
                                        {formatPremium(footerTotals.assocPremium)}
                                    </span>
                                </td>
                            </Fragment>
                        ))}

                        <td
                            className="border-secondary md:sticky md:right-0 md:z-10 border-b border-l bg-secondary"
                            style={{ width: TRASH_COL_W, minWidth: TRASH_COL_W }}
                        />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

/** One editable, not-yet-saved Survey row. Facility via the catalog
 *  autocomplete (links `facility_id` + fills name/state); the metrics feed a
 *  single survey POSTed on Save. Older year groups are blank — Add Row creates
 *  one survey whose Last Survey date sets its real year. */
function SurveyDraftRow({
    draft,
    index,
    years,
    showErrors,
    onChange,
    onRemove,
    onPickFacility,
}: {
    draft: NewSurveyDraft;
    index: number;
    years: string[];
    showErrors: boolean;
    onChange: (id: string, patch: Partial<NewSurveyDraft>) => void;
    onRemove: (id: string) => void;
    onPickFacility: (id: string, facility: CatalogFacility) => void;
}) {
    const set = (patch: Partial<NewSurveyDraft>) => onChange(draft.id, patch);
    const facilityInvalid = showErrors && !draft.facilityId;
    const dateInvalid = draft.lastSurvey !== "" && formDateToIso(draft.lastSurvey) === null;
    return (
        <tr className="text-tertiary">
            <td
                className={cx(STICKY_CELL["left-0"], "border-secondary border-b px-6 py-3")}
                style={{ width: COST_COLUMNS[0].width, minWidth: COST_COLUMNS[0].width }}
            >
                {index}
            </td>
            <td
                className={cx(STICKY_CELL["left-80"], "border-secondary border-b px-3 py-2")}
                style={{ width: COST_COLUMNS[1].width, minWidth: COST_COLUMNS[1].width }}
            >
                <InvalidShell isInvalid={facilityInvalid} errorMessage="Select a facility from the list." reserveSpace={false}>
                    <FacilityNameAutocomplete
                        ariaLabel="Facility Name"
                        placeholder="Type Facility Name"
                        value={draft.facilityName}
                        onChange={(v) => set({ facilityName: v })}
                        onSelect={(cf) => onPickFacility(draft.id, cf)}
                        isInvalid={facilityInvalid}
                    />
                </InvalidShell>
                <StickyColDivider mdOnly />
            </td>
            <td className="border-secondary border-b px-3 py-2" style={{ width: COST_COLUMNS[2].width, minWidth: COST_COLUMNS[2].width }}>
                <SearchSelect
                    size="sm"
                    aria-label="State"
                    placeholder="—"
                    selectedKey={draft.state || null}
                    items={US_STATES}
                    onSelectionChange={(k) => set({ state: (k as string) ?? "" })}
                    popoverWidthMul={1.2}
                >
                    {(item) => <SearchSelect.Item id={item.id}>{item.label}</SearchSelect.Item>}
                </SearchSelect>
            </td>
            <td className="border-secondary border-b px-3 py-2" style={{ width: COST_COLUMNS[3].width, minWidth: COST_COLUMNS[3].width }}>
                <EditableTextCell ariaLabel="# Fines" inputMode="numeric" value={draft.fineCount} placeholder="0" sanitize={sanitizeCount} onChange={(v) => set({ fineCount: v })} />
            </td>
            <td className="border-secondary border-b px-3 py-2" style={{ width: COST_COLUMNS[4].width, minWidth: COST_COLUMNS[4].width }}>
                <EditableTextCell ariaLabel="Total Fines" value={draft.totalFines} placeholder="$0.00" sanitize={sanitizeMoney} onChange={(v) => set({ totalFines: v })} />
            </td>
            {years.map((year, yIdx) =>
                yIdx === 0 ? (
                    <Fragment key={year}>
                        <td className="border-secondary border-b border-l px-3 py-2" style={{ width: YEAR_COLUMNS[0].width, minWidth: YEAR_COLUMNS[0].width }}>
                            <EditableDateCell
                                ariaLabel="Last Survey"
                                value={draft.lastSurvey}
                                onChange={(v) => set({ lastSurvey: v })}
                                isInvalid={dateInvalid}
                                errorMessage="The date format is incorrect. Please enter the date as MM/DD/YYYY"
                            />
                        </td>
                        <td className="border-secondary border-b px-3 py-2" style={{ width: YEAR_COLUMNS[1].width, minWidth: YEAR_COLUMNS[1].width }}>
                            <EditableTextCell ariaLabel="Severity Citations" inputMode="numeric" value={draft.severityCitations} placeholder="0" sanitize={sanitizeCount} onChange={(v) => set({ severityCitations: v })} />
                        </td>
                        <td className="border-secondary border-b px-3 py-2" style={{ width: YEAR_COLUMNS[2].width, minWidth: YEAR_COLUMNS[2].width }}>
                            <EditableTextCell ariaLabel="# Citations" inputMode="numeric" value={draft.citationCount} placeholder="0" sanitize={sanitizeCount} onChange={(v) => set({ citationCount: v })} />
                        </td>
                        <td className="border-secondary border-b px-3 py-2" style={{ width: YEAR_COLUMNS[3].width, minWidth: YEAR_COLUMNS[3].width }}>
                            <EditableTextCell ariaLabel="# Complaints" inputMode="numeric" value={draft.complaintCount} placeholder="0" sanitize={sanitizeCount} onChange={(v) => set({ complaintCount: v })} />
                        </td>
                        <td className="border-secondary border-b px-3 py-2" style={{ width: YEAR_COLUMNS[4].width, minWidth: YEAR_COLUMNS[4].width }}>
                            <EditableTextCell ariaLabel="Explanation" value={draft.explanation} placeholder="Text" onChange={(v) => set({ explanation: v })} />
                        </td>
                    </Fragment>
                ) : (
                    YEAR_COLUMNS.map((col, cIdx) => (
                        <td
                            key={`${year}.${col.id}`}
                            className={cx("border-secondary text-placeholder border-b px-6 py-3", cIdx === 0 && "border-l")}
                            style={{ width: col.width, minWidth: col.width }}
                        >
                            —
                        </td>
                    ))
                ),
            )}
            <td
                className={cx(STICKY_RIGHT_CELL, "border-secondary border-b border-l px-3 py-3")}
                style={{ width: TRASH_COL_W, minWidth: TRASH_COL_W }}
            >
                <TrashButton label="Remove new row" onClick={() => onRemove(draft.id)} />
            </td>
        </tr>
    );
}

// ─── Acuity table ───────────────────────────────────────────────────────────

/** Year-column render slot — the confirmed year columns plus (at most one)
 *  in-progress "new column" whose header is the year input. */
type AcuityYearCol = { kind: "year"; year: string } | { kind: "draft" };

interface AcuityTableProps {
    rows: AcuityRow[];
    drafts: NewAcuityDraft[];
    /** Year columns to render, in the user's (persisted) display order. */
    years: string[];
    /** In-progress new-column header input (null = none). */
    yearDraft: { value: string; insertAt: number } | null;
    yearDraftError: string | null;
    /** Row id → rendered years with no data (Missing Data flags). */
    missing: ReadonlyMap<string, string[]>;
    sort: SortState<AcuitySortField> | null;
    onSort: (field: AcuitySortField) => void;
    onDelete: (id: string) => void;
    /** Archive state + toggles for the row's underlying facility (OR-1431). */
    isRowArchived: (row: { facilityId: string; facilityName: string }) => boolean;
    /** Resolves the facility behind a row, if any — undefined rows get Delete
     *  only, since there's nothing to archive. */
    getFacilityForRow: (row: { facilityId: string; facilityName: string }) => Facility | undefined;
    onArchiveFacility: (facilityId: string, isExcluded: boolean) => void;
    /** Edit a loaded sub-row's census cell (care type × year). */
    onCensusChange: (rowId: string, field: AcuityBedField, year: string, value: string) => void;
    onDraftChange: (id: string, patch: Partial<NewAcuityDraft>) => void;
    onDraftValue: (id: string, year: string, value: string) => void;
    onDraftRemove: (id: string) => void;
    onDraftPickFacility: (id: string, facility: CatalogFacility) => void;
    onYearDraftChange: (value: string) => void;
    onYearDraftCommit: () => void;
    onYearDraftCancel: () => void;
    onMoveYear: (year: string, dir: -1 | 1) => void;
    onAddYearAt: (nearYear: string, side: "left" | "right") => void;
    onDeleteYear: (year: string) => void;
    onReorderYear: (source: string, target: string) => void;
    showErrors: boolean;
}

function AcuityTable({
    rows,
    drafts,
    years,
    yearDraft,
    yearDraftError,
    missing,
    sort,
    onSort,
    onDelete,
    isRowArchived,
    getFacilityForRow,
    onArchiveFacility,
    onCensusChange,
    onDraftChange,
    onDraftValue,
    onDraftRemove,
    onDraftPickFacility,
    onYearDraftChange,
    onYearDraftCommit,
    onYearDraftCancel,
    onMoveYear,
    onAddYearAt,
    onDeleteYear,
    onReorderYear,
    showErrors,
}: AcuityTableProps) {
    // Confirmed year columns + the (single) draft column at its insert slot.
    const yearCols: AcuityYearCol[] = years.map((year) => ({ kind: "year" as const, year }));
    if (yearDraft) yearCols.splice(Math.min(yearDraft.insertAt, yearCols.length), 0, { kind: "draft" });

    // The per-year Beds Sum footer counts covered facilities only (OR-1431).
    // The backend already filters archived locations out of the occupancy that
    // feeds the loss-run summary, so a footer that still added them would show
    // a census the pricing side doesn't use.
    const activeRows = rows.filter((row) => !isRowArchived(row));

    return (
        <div className="atlas-scrollbar overflow-auto lg:min-h-0 lg:flex-1">
            <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
                <thead>
                    <tr className="bg-secondary">
                        {ACUITY_BASE_COLUMNS.map((col) => (
                            <th
                                key={col.id}
                                scope="col"
                                className={cx(
                                    "border-secondary border-b px-6 py-2 text-left",
                                    col.sticky && STICKY_HEAD[col.sticky],
                                )}
                                style={{ width: col.width, minWidth: col.width }}
                            >
                                {col.sortable ? (
                                    <SortHeader field={col.id as AcuitySortField} sort={sort} onSortChange={onSort}>
                                        {col.label}
                                    </SortHeader>
                                ) : (
                                    <span className="text-quaternary text-xs font-semibold whitespace-nowrap">{col.label}</span>
                                )}
                            </th>
                        ))}
                        {yearCols.map((col) =>
                            col.kind === "year" ? (
                                <AcuityYearHeader
                                    key={col.year}
                                    year={col.year}
                                    isFirst={years[0] === col.year}
                                    isLast={years[years.length - 1] === col.year}
                                    sort={sort}
                                    onSort={onSort}
                                    onMove={onMoveYear}
                                    onAddAt={onAddYearAt}
                                    onDelete={onDeleteYear}
                                    onReorder={onReorderYear}
                                />
                            ) : (
                                <AcuityYearDraftHeader
                                    key="year-draft"
                                    value={yearDraft?.value ?? ""}
                                    error={yearDraftError}
                                    onChange={onYearDraftChange}
                                    onCommit={onYearDraftCommit}
                                    onCancel={onYearDraftCancel}
                                />
                            ),
                        )}
                        <th
                            className={cx(STICKY_RIGHT_HEAD, "border-secondary border-b")}
                            style={{ width: TRASH_COL_W, minWidth: TRASH_COL_W }}
                            aria-hidden="true"
                        />
                    </tr>
                </thead>

                <tbody>
                    {/* One block per facility (OR-1328 design update): no collapse,
                      * no "All" summary row — the four care-type rows are always
                      * shown. The identity columns (#, Facility Name + missing-data
                      * warning, State, trash) span the block via rowSpan and center
                      * vertically; only the last care-type row carries the bottom
                      * border so there are no internal separators within a block. */}
                    {rows.map((row) => {
                        const missingYears = missing.get(row.id);
                        // An archived facility's whole block dims + strikes through
                        // (OR-1431). The backend already drops its occupancy from
                        // the loss-run summary, so leaving it looking live here
                        // would put the two out of step.
                        const archived = isRowArchived(row);
                        const facilityForRow = getFacilityForRow(row);
                        return (
                            <Fragment key={row.id}>
                                {ACUITY_CARE_TYPES.map((careType, ci) => {
                                    const isFirst = ci === 0;
                                    const cellBorder = ci === ACUITY_CARE_TYPES.length - 1 ? "border-b" : "";
                                    return (
                                        <tr
                                            key={`${row.id}.${careType.field}`}
                                            className={cx("text-tertiary", archived && ARCHIVED_ROW)}
                                        >
                                            {isFirst && (
                                                <td
                                                    rowSpan={ACUITY_CARE_TYPES.length}
                                                    className={cx(STICKY_CELL["left-0"], "border-secondary border-b px-4 py-4 align-top")}
                                                    style={{ width: ACUITY_BASE_COLUMNS[0].width, minWidth: ACUITY_BASE_COLUMNS[0].width }}
                                                >
                                                    {row.index}
                                                </td>
                                            )}
                                            {isFirst && (
                                                <td
                                                    rowSpan={ACUITY_CARE_TYPES.length}
                                                    className={cx(STICKY_CELL["left-80"], "border-secondary border-b px-6 py-4 align-top")}
                                                    style={{ width: ACUITY_BASE_COLUMNS[1].width, minWidth: ACUITY_BASE_COLUMNS[1].width }}
                                                >
                                                    <div className="flex items-start gap-1.5">
                                                        <div
                                                            className="max-h-20 min-w-0 overflow-y-auto whitespace-normal [overflow-wrap:anywhere] leading-snug"
                                                            data-autotest="regulatory-acuity-tab-facility-name-cell"
                                                        >
                                                            {row.facilityName || "—"}
                                                        </div>
                                                        {missingYears && (
                                                            <Tooltip title={`Missing ${missingYears.join(", ")}`} placement="top" arrow>
                                                                <TooltipTrigger
                                                                    aria-label={`Missing data for ${missingYears.join(", ")}`}
                                                                    className="mt-0.5 inline-flex shrink-0 cursor-help"
                                                                    excludeFromTabOrder
                                                                >
                                                                    <AlertCircle aria-hidden="true" className="text-utility-yellow-500 size-4" />
                                                                </TooltipTrigger>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                            {isFirst && (
                                                <td
                                                    rowSpan={ACUITY_CARE_TYPES.length}
                                                    className="border-secondary border-b px-6 py-4 align-top"
                                                    style={{ width: ACUITY_BASE_COLUMNS[2].width, minWidth: ACUITY_BASE_COLUMNS[2].width }}
                                                    data-autotest="regulatory-acuity-tab-state-cell"
                                                >
                                                    {row.state || "—"}
                                                </td>
                                            )}
                                            <td
                                                className={cx("border-secondary px-6 py-3", cellBorder)}
                                                style={{ width: ACUITY_BASE_COLUMNS[3].width, minWidth: ACUITY_BASE_COLUMNS[3].width }}
                                            >
                                                {careType.label}
                                            </td>
                                            {yearCols.map((col, i) =>
                                                col.kind === "year" ? (
                                                    <td
                                                        key={`${row.id}.${careType.field}.${col.year}`}
                                                        className={cx("border-secondary px-3 py-2", cellBorder)}
                                                        style={{ width: ACUITY_YEAR_COL_W, minWidth: ACUITY_YEAR_COL_W }}
                                                    >
                                                        <EditableTextCell
                                                            ariaLabel={`${careType.label} ${col.year} census`}
                                                            inputMode="numeric"
                                                            value={row.census[careType.field]?.[col.year] ?? ""}
                                                            placeholder="—"
                                                            align="right"
                                                            sanitize={sanitizeCount}
                                                            onChange={(v) => onCensusChange(row.id, careType.field, col.year, v)}
                                                            data-autotest={`regulatory-acuity-tab-${careType.field.replace(/_/g, "-")}-${col.year}-cell`}
                                                        />
                                                    </td>
                                                ) : (
                                                    <td
                                                        key={`${row.id}.${careType.field}.draft.${i}`}
                                                        className={cx("border-secondary text-placeholder px-6 py-3 text-right", cellBorder)}
                                                        style={{ width: ACUITY_YEAR_COL_W, minWidth: ACUITY_YEAR_COL_W }}
                                                    >
                                                        —
                                                    </td>
                                                ),
                                            )}
                                            {isFirst && (
                                                <td
                                                    rowSpan={ACUITY_CARE_TYPES.length}
                                                    className={cx(STICKY_RIGHT_CELL, "border-secondary border-b px-3 py-4 align-middle")}
                                                    style={{ width: TRASH_COL_W, minWidth: TRASH_COL_W }}
                                                >
                                                    <FacilityRowActionsMenu
                                                        facilityName={row.facilityName}
                                                        isArchived={archived}
                                                        canArchive={facilityForRow !== undefined}
                                                        onArchive={
                                                            facilityForRow
                                                                ? () => onArchiveFacility(facilityForRow.id, true)
                                                                : undefined
                                                        }
                                                        onRestore={
                                                            facilityForRow
                                                                ? () => onArchiveFacility(facilityForRow.id, false)
                                                                : undefined
                                                        }
                                                        onDelete={() => onDelete(row.id)}
                                                        deleteTitle="Delete occupancy"
                                                        deleteDescription="Delete this facility's occupancy records from the submission. This action cannot be undone."
                                                        data-autotest="regulatory-acuity-tab-row-actions-trigger"
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </Fragment>
                        );
                    })}

                    {/* User-added editable rows — persisted merge-aware
                      * (PATCH existing (facility, year) records / POST new)
                      * on Save & Continue. */}
                    {drafts.map((draft, i) => (
                        <AcuityDraftRow
                            key={draft.id}
                            draft={draft}
                            index={rows.length + i + 1}
                            yearCols={yearCols}
                            showErrors={showErrors}
                            alreadyExists={!!draft.catalog && rows.some((r) => acuityRowMatchesCatalog(r, draft.catalog!))}
                            onChange={onDraftChange}
                            onValue={onDraftValue}
                            onRemove={onDraftRemove}
                            onPickFacility={onDraftPickFacility}
                        />
                    ))}
                </tbody>

                {/* Beds Sum — per-year totals across every facility's "All"
                  * (+ draft-row values), dash when nothing contributes. */}
                <tfoot>
                    <tr className="bg-secondary font-semibold">
                        <td
                            className="border-secondary md:sticky md:left-0 md:z-10 bg-secondary border-b px-6 py-3"
                            style={{ width: ACUITY_BASE_COLUMNS[0].width, minWidth: ACUITY_BASE_COLUMNS[0].width }}
                        />
                        <td
                            className="border-secondary md:sticky md:left-[80px] md:z-10 bg-secondary border-b px-6 py-3"
                            style={{ width: ACUITY_BASE_COLUMNS[1].width, minWidth: ACUITY_BASE_COLUMNS[1].width }}
                        />
                        <td className="border-secondary border-b" style={{ width: ACUITY_BASE_COLUMNS[2].width, minWidth: ACUITY_BASE_COLUMNS[2].width }} />
                        <td className="border-secondary border-b" style={{ width: ACUITY_BASE_COLUMNS[3].width, minWidth: ACUITY_BASE_COLUMNS[3].width }} />
                        {yearCols.map((col, i) =>
                            col.kind === "year" ? (
                                <td
                                    key={`sum.${col.year}`}
                                    className="text-primary border-secondary border-b px-6 py-3 text-right tabular-nums"
                                    style={{ width: ACUITY_YEAR_COL_W, minWidth: ACUITY_YEAR_COL_W }}
                                    aria-label={`Beds sum ${col.year}`}
                                    data-autotest={`regulatory-acuity-tab-beds-sum-${col.year}-total`}
                                >
                                    {bedsSumForYear(activeRows, drafts.map((d) => d.values), col.year) ?? "—"}
                                </td>
                            ) : (
                                <td key={`sum.draft.${i}`} className="text-placeholder border-secondary border-b px-6 py-3 text-right" style={{ width: ACUITY_YEAR_COL_W, minWidth: ACUITY_YEAR_COL_W }}>
                                    —
                                </td>
                            ),
                        )}
                        <td
                            className="border-secondary md:sticky md:right-0 md:z-10 bg-secondary border-b"
                            style={{ width: TRASH_COL_W, minWidth: TRASH_COL_W }}
                        />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

/** A confirmed year column header: sortable label (draggable for manual
 *  reordering) + a hover context menu with Move Left/Right, Column
 *  Left/Right and Delete Column (OR-1328). */
function AcuityYearHeader({
    year,
    isFirst,
    isLast,
    sort,
    onSort,
    onMove,
    onAddAt,
    onDelete,
    onReorder,
}: {
    year: string;
    isFirst: boolean;
    isLast: boolean;
    sort: SortState<AcuitySortField> | null;
    onSort: (field: AcuitySortField) => void;
    onMove: (year: string, dir: -1 | 1) => void;
    onAddAt: (nearYear: string, side: "left" | "right") => void;
    onDelete: (year: string) => void;
    onReorder: (source: string, target: string) => void;
}) {
    const menuItemClass =
        "text-secondary hover:bg-secondary_hover focus:bg-secondary_hover data-disabled:text-fg-disabled data-disabled:cursor-not-allowed flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm font-semibold outline-none";
    return (
        <th
            scope="col"
            className="group border-secondary border-b px-3 py-2 text-left"
            style={{ width: ACUITY_YEAR_COL_W, minWidth: ACUITY_YEAR_COL_W }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
                e.preventDefault();
                const source = e.dataTransfer.getData("text/plain");
                if (source) onReorder(source, year);
            }}
        >
            <span
                className="inline-flex items-center gap-0.5"
                draggable
                onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", year);
                    e.dataTransfer.effectAllowed = "move";
                }}
            >
                <SortHeader field={year as AcuitySortField} sort={sort} onSortChange={onSort}>
                    {year}
                </SortHeader>
                <AriaMenuTrigger>
                    <AriaButton
                        aria-label={`Column options for ${year}`}
                        className="text-fg-quaternary hover:text-fg-secondary focus-visible:ring-focus-ring inline-flex size-5 cursor-pointer items-center justify-center rounded opacity-0 outline-none transition duration-100 ease-linear group-hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 aria-expanded:opacity-100"
                    >
                        <DotsVertical className="size-3.5" />
                    </AriaButton>
                    <AriaPopover placement="bottom end" offset={4} className="z-50 entering:animate-in entering:fade-in entering:zoom-in-95">
                        <AriaMenu
                            aria-label={`Column actions for ${year}`}
                            className="bg-primary border-secondary min-w-44 rounded-lg border p-1 shadow-lg outline-none"
                            onAction={(key) => {
                                if (key === "move-left") onMove(year, -1);
                                else if (key === "move-right") onMove(year, 1);
                                else if (key === "column-left") onAddAt(year, "left");
                                else if (key === "column-right") onAddAt(year, "right");
                                else if (key === "delete") onDelete(year);
                            }}
                        >
                            <AriaMenuItem id="move-left" isDisabled={isFirst} className={menuItemClass}>
                                <ArrowLeft aria-hidden="true" className="size-4" />
                                Move Left
                            </AriaMenuItem>
                            <AriaMenuItem id="move-right" isDisabled={isLast} className={menuItemClass}>
                                <ArrowRight aria-hidden="true" className="size-4" />
                                Move Right
                            </AriaMenuItem>
                            <AriaMenuItem id="column-left" className={menuItemClass}>
                                <Plus aria-hidden="true" className="size-4" />
                                Column Left
                            </AriaMenuItem>
                            <AriaMenuItem id="column-right" className={menuItemClass}>
                                <Plus aria-hidden="true" className="size-4" />
                                Column Right
                            </AriaMenuItem>
                            <AriaMenuItem id="delete" className={cx(menuItemClass, "text-error-primary")}>
                                <Trash02 aria-hidden="true" className="size-4" />
                                Delete Column
                            </AriaMenuItem>
                        </AriaMenu>
                    </AriaPopover>
                </AriaMenuTrigger>
            </span>
        </th>
    );
}

/** The in-progress "new year column" header: a focused 4-digit input.
 *  Enter/blur confirms; Escape (or blur while empty) reverts; duplicate /
 *  invalid years keep the input in an error state with the message shown
 *  in a floating chip (design: "This year has already been added"). */
function AcuityYearDraftHeader({
    value,
    error,
    onChange,
    onCommit,
    onCancel,
}: {
    value: string;
    error: string | null;
    onChange: (value: string) => void;
    onCommit: () => void;
    onCancel: () => void;
}) {
    const thRef = useRef<HTMLTableCellElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Draw the user's eye to the new column: scroll it into view on mount.
    useEffect(() => {
        thRef.current?.scrollIntoView({ inline: "nearest", block: "nearest" });
    }, []);

    // A failed commit (duplicate/invalid year) keeps the column: put the
    // caret back so the user can fix the value in place (per design).
    useEffect(() => {
        if (error) inputRef.current?.focus();
    }, [error]);

    return (
        <th
            ref={thRef}
            scope="col"
            className="border-secondary relative border-b px-3 py-2 text-left"
            style={{ width: ACUITY_YEAR_COL_W, minWidth: ACUITY_YEAR_COL_W }}
        >
            {/* Floating chip BELOW the input (the header is the scroll
              * container's first row, so anything above gets clipped) and
              * RIGHT-anchored (the new column sits at the table's right
              * edge, so a left-anchored chip runs off the container). */}
            {error && (
                <span
                    role="alert"
                    className="bg-primary-solid text-primary_on-brand absolute top-full right-0 z-30 mt-1 w-max max-w-60 rounded-md px-2.5 py-1.5 text-xs font-medium whitespace-normal shadow-lg"
                >
                    {error}
                </span>
            )}
            <input
                ref={inputRef}
                autoFocus
                type="text"
                inputMode="numeric"
                maxLength={4}
                aria-label="New year column"
                aria-invalid={error ? true : undefined}
                value={value}
                placeholder="Year"
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") onCommit();
                    else if (e.key === "Escape") onCancel();
                }}
                onBlur={onCommit}
                className={cx(
                    "bg-primary text-primary placeholder:text-placeholder h-7 w-full rounded-md border px-2 text-xs font-semibold outline-none",
                    error ? "border-error focus:border-error" : "border-brand focus:border-brand",
                )}
            />
        </th>
    );
}

/** One editable, not-yet-saved Acuity row: facility (catalog autocomplete) +
 *  care Type + State + a census value per year. Each filled year POSTs an
 *  occupancy on Save & Continue. */
function AcuityDraftRow({
    draft,
    index,
    yearCols,
    showErrors,
    alreadyExists,
    onChange,
    onValue,
    onRemove,
    onPickFacility,
}: {
    draft: NewAcuityDraft;
    index: number;
    yearCols: AcuityYearCol[];
    showErrors: boolean;
    /** The picked facility is already a row in the table — warn + block save
     *  so its data doesn't silently merge into the existing row (OR-1417). */
    alreadyExists: boolean;
    onChange: (id: string, patch: Partial<NewAcuityDraft>) => void;
    onValue: (id: string, year: string, value: string) => void;
    onRemove: (id: string) => void;
    onPickFacility: (id: string, facility: CatalogFacility) => void;
}) {
    const years = yearCols.filter((c): c is Extract<AcuityYearCol, { kind: "year" }> => c.kind === "year").map((c) => c.year);
    // "Already in the table" shows the moment a duplicate facility is picked
    // (not gated by showErrors); "select a facility" only after a save attempt.
    const facilityInvalid = alreadyExists || (showErrors && !draft.facilityId);
    const facilityError = alreadyExists
        ? "This facility is already in the Acuity table — edit its row instead."
        : "Select a facility from the list.";
    const typeInvalid = showErrors && !draft.type;
    const valuesInvalid = showErrors && !years.some((y) => toIntOrNull(draft.values[y] ?? "") !== null);
    return (
        <tr className="text-tertiary">
            <td
                className={cx(STICKY_CELL["left-0"], "border-secondary border-b px-6 py-3")}
                style={{ width: ACUITY_BASE_COLUMNS[0].width, minWidth: ACUITY_BASE_COLUMNS[0].width }}
            >
                {index}
            </td>
            <td
                className={cx(STICKY_CELL["left-80"], "border-secondary border-b px-3 py-2")}
                style={{ width: ACUITY_BASE_COLUMNS[1].width, minWidth: ACUITY_BASE_COLUMNS[1].width }}
            >
                <InvalidShell isInvalid={facilityInvalid} errorMessage={facilityError} reserveSpace={false}>
                    <FacilityNameAutocomplete
                        ariaLabel="Facility Name"
                        placeholder="Type Facility Name"
                        value={draft.facilityName}
                        onChange={(v) => onChange(draft.id, { facilityName: v })}
                        onSelect={(cf) => onPickFacility(draft.id, cf)}
                        isInvalid={facilityInvalid}
                    />
                </InvalidShell>
                <StickyColDivider mdOnly />
            </td>
            <td className="border-secondary border-b px-3 py-2" style={{ width: ACUITY_BASE_COLUMNS[2].width, minWidth: ACUITY_BASE_COLUMNS[2].width }}>
                <SearchSelect
                    size="sm"
                    aria-label="State"
                    placeholder="—"
                    selectedKey={draft.state || null}
                    items={US_STATES}
                    onSelectionChange={(k) => onChange(draft.id, { state: (k as string) ?? "" })}
                    popoverWidthMul={1.2}
                >
                    {(item) => <SearchSelect.Item id={item.id}>{item.label}</SearchSelect.Item>}
                </SearchSelect>
            </td>
            <td className="border-secondary border-b px-3 py-2" style={{ width: ACUITY_BASE_COLUMNS[3].width, minWidth: ACUITY_BASE_COLUMNS[3].width }}>
                <InvalidShell isInvalid={typeInvalid} errorMessage="Select a type.">
                    <SearchSelect
                        size="sm"
                        aria-label="Type"
                        placeholder="—"
                        selectedKey={draft.type || null}
                        items={[...ACUITY_TYPE_OPTIONS]}
                        onSelectionChange={(k) => onChange(draft.id, { type: (k as AcuityBedField) ?? "" })}
                        popoverWidthMul={1.4}
                        isInvalid={typeInvalid}
                    >
                        {(item) => <SearchSelect.Item id={item.id}>{item.label}</SearchSelect.Item>}
                    </SearchSelect>
                </InvalidShell>
            </td>
            {yearCols.map((col, i) =>
                col.kind === "year" ? (
                    <td key={`${draft.id}.${col.year}`} className="border-secondary border-b px-3 py-2" style={{ width: ACUITY_YEAR_COL_W, minWidth: ACUITY_YEAR_COL_W }}>
                        <EditableTextCell
                            ariaLabel={`${col.year} census`}
                            inputMode="numeric"
                            value={draft.values[col.year] ?? ""}
                            placeholder="0"
                            align="right"
                            sanitize={sanitizeCount}
                            onChange={(v) => onValue(draft.id, col.year, v)}
                            isInvalid={col.year === years[0] && valuesInvalid}
                            errorMessage={col.year === years[0] ? "Enter at least one year value." : undefined}
                        />
                    </td>
                ) : (
                    <td key={`${draft.id}.draft.${i}`} className="border-secondary text-placeholder border-b px-6 py-3 text-right" style={{ width: ACUITY_YEAR_COL_W, minWidth: ACUITY_YEAR_COL_W }}>
                        —
                    </td>
                ),
            )}
            <td
                className={cx(STICKY_RIGHT_CELL, "border-secondary border-b border-l px-3 py-3")}
                style={{ width: TRASH_COL_W, minWidth: TRASH_COL_W }}
            >
                <TrashButton label="Remove new row" onClick={() => onRemove(draft.id)} />
            </td>
        </tr>
    );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function DateField({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-tertiary text-xs font-normal">{label}</span>
            <span className="text-secondary inline-flex items-center gap-1.5 text-xs font-semibold">
                <Calendar aria-hidden="true" className="text-fg-quaternary size-4" />
                {value || <span className="text-placeholder font-normal">—</span>}
            </span>
        </div>
    );
}

function TrashButton({
    label,
    onClick,
    disabled,
    disabledHint,
}: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    disabledHint?: string;
}) {
    return (
        <button
            type="button"
            aria-label={label}
            onClick={onClick}
            disabled={disabled}
            title={disabled ? disabledHint : undefined}
            className={cx(
                "inline-flex size-8 items-center justify-center rounded-md transition duration-100 ease-linear",
                disabled
                    ? "text-fg-disabled cursor-not-allowed"
                    : "text-fg-quaternary hover:text-fg-secondary hover:bg-secondary_hover cursor-pointer",
            )}
        >
            <Trash02 className="size-4" />
        </button>
    );
}

interface SortHeaderProps<F extends string> {
    field: F;
    sort: SortState<F> | null;
    onSortChange: (field: F) => void;
    children: React.ReactNode;
}

function SortHeader<F extends string>({ field, sort, onSortChange, children }: SortHeaderProps<F>) {
    const active = sort?.field === field;
    const dir = active ? sort!.dir : null;
    const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ChevronSelectorVertical;
    const ariaSort = !active ? "none" : dir === "asc" ? "ascending" : "descending";
    return (
        <button
            type="button"
            onClick={() => onSortChange(field)}
            aria-sort={ariaSort}
            className="text-quaternary inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold whitespace-nowrap outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-focus-ring"
        >
            {children}
            <Icon
                aria-hidden="true"
                className={cx("size-3.5 shrink-0", active ? "text-fg-secondary" : "text-fg-quaternary")}
            />
        </button>
    );
}

